import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEffectOnActive } from 'keepalive-for-react';
import dayjs, { Dayjs } from 'dayjs';
import XLSX from 'xlsx-js-style';
import { saveAs } from 'file-saver';
import { useQueryClient } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { Col, convertToKRW, getItemWithExpiration, Row } from '../../lib/utils';
import { BoldText, MediumText, RegularText } from '../../lib/Text';
import { colors } from '../../lib/colors';
import SvgIcon from '../../components/SvgIcon';
import Table from '../../components/Table/Table';
import Modal from '../../components/modal/Modal';
import { TooltipWrapper } from '../../components/TooltipWrapper';
import { CodeValueType } from '../../types/types';
import { TooltipIcon } from './SettlementCalendar';
import SessionReportMembershipInfo from './components/SessionReportMembershipInfo';
import { hasReportReviewWork, screenReportText, ColumnGlossary, DetailToggle, formatReportNumber, MemberRemainingSessions, MembershipSessionUsage, ReportTableNumber, ReportColumnTitle, ReportExplanationContext, ReportMonthContext, REVIEW_TONES, useReportDisclosure } from './sessionReportPresentation';
import SessionReportGuide, { useSessionReportGuide } from './SessionReportGuide';
import LessonMovePanel, { LessonMoveCompletion } from './SessionReportLessonMove';
import { BEFORE_ISSUE_FLAGS, BeforeIssueGuidance } from './SessionReportBeforeIssue';
import { SessionReportOptions, SessionReportWarnings } from './SessionReportOptions';
import { useSessionReportResolution } from './SessionReportResolution';
import { isOpenSupport, supportListKey } from '../../features/sessionReport/support';
import { getSupportGuidance, rowSupportPresentation, SupportAction, SupportBadge, supportBadgeLabel, SupportContext, SupportSummary, useRowSupport, useSupportList } from './SessionReportSupport';
import { hasResolutionRefresh } from '../../features/sessionReport/resolution';
import { SessionReportReviewList } from './SessionReportReviewList';
import { CoachAvatar, GoodStatus, reportCard, reportPill, reportTable, ReportIcon, ReviewDrawer, ReviewDetailLayer, ReviewPending, SessionFlowHero, summaryFlowDifference } from './SessionReportSessionView';
import { SessionReportEvidenceDrawer } from './SessionReportEvidenceDrawer';
import { FlowEvidenceSelection } from '../../features/sessionReport/flowEvidence';
import { SessionCountFlow } from './SessionCountFlow';
import './SessionReportPage.css';
import {
    buildSessionReportFileName,
    buildSessionReportWorkbook,
    collectConsistentExport,
    fetchSessionReportFresh,
    sessionReportKeys,
    isSessionReportTimeout,
    SESSION_REPORT_TOO_MANY_MESSAGE,
    SESSION_REPORT_TOO_MANY_REASON,
    useCoachSummarySync,
    useExportRun,
    decideExportOutcome,
    exportPrincipalKey,
    PRINCIPAL_CHANGED_ABORT,
    describeFlag,
    hasMembershipSessionUsage,
    describeMembershipSessionOverage,
    formatHandoffs,
    formatKst,
    isMismatchRow,
    isSessionlessRow,
    isRepMovedRow,
    describeRowFlag,
    describeRepMoved,
    isCompanionRepMove,
    isCheckNeededRow,
    screenCheckNeededCount,
    mismatchCount,
    checkFlagCounts,
    unpaidReviewCount,
    listCheckFlags,
    describeFlagShort,
    describeFlagAction,
    describeReviewIssue,
    describeReviewAction,
    UNKNOWN_FLAG_KEY,
    flagReviewLevel,
    rowReviewReasons,
    REVIEW_LEVEL_ORDER,
    topReviewLevel,
    HANDOFF_FLAGS,
    HANDOFF_EXPLAINER,
    isUnlimitedRow,
    isUnpaidUnknown,
    hasInOutSplit,
    describeSummaryRefetchFailure,
    REFETCH_FAILED_EXPORT_BLOCKED_TEXT,
    visibleFlags,
    INFO_ONLY_FLAGS,
    pickCoachFromReport,
    SESSION_REPORT_SHEETS,
    coachHasCheckFlag,
    SessionReport,
    SessionReportCoach,
    SessionReportMembership,
    SessionReportRow,
    SessionReportSheetKey,
    SessionReportSummaryBase,
    SessionReportSheetTotals,
    UNPAID_UNKNOWN_TEXT,
    useSessionReport,
    useSessionReportLessonsAvailability,
} from '../../features/sessionReport';

/**
 * 정산 > 세션 현황 (읽기 전용, 정산 권한 있는 모든 센터 — 10/19 범용화)
 *
 * 헬스보이짐이 매달 손으로 만들던 「총 페이롤」(총괄 + 강사별 유효기간내/외/유령)을 서버 계산값으로 보여 주고 내려받게 한다.
 * 화면은 센터 데이터에 맞춰 IN·OUT 처럼 일반 센터에서 의미 없는 열을 숨기고, 엑셀은 고객 원본 양식(열 위치)을 그대로 지킨다.
 * 금액·회차·합계·검산·시트 분류는 서버 값 그대로 쓴다(BACKEND_HANDOFF_STAGE1.md).
 * 요약 흐름과 수강권 카드의 단계 산수만 표시용으로 비교하며 서버 값이나 검산 판정에 반영하지 않는다.
 *
 * 조회 구조(계약 §0): 강사 미지정 요약을 받은 뒤 손볼 일이 있는 강사의 상세를 동시 2개까지 받는다. 회원별 표는 고른 강사를 부른다.
 * 엑셀은 강사 수만큼 순차로 불러 모은 뒤 만들고, 한 강사라도 실패하면 파일을 만들지 않는다.
 * 띠·강사 표·센터 합계는 서버 요약을 쓰고, 서랍 단계도 같은 회원×시트 행 수로 센다.
 */

type Props = {
    centerId?: string;
    centerName?: string;
    date: string;
    onMonthNavigate: (date: Date | string | Dayjs) => void;
    /** 정산 화면이 이미 불러 둔 강사 목록(퇴사 포함). 요약 조회가 시간 초과일 때 강사를 고르는 데 쓴다. */
    coachList?: CodeValueType<string>[];
    createAlert?: (title: string, content: string, isError: boolean) => void;
    /** 알림센터 「라포 확인」 카드로 들어올 때 고를 강사. nonce 가 바뀔 때마다 한 번 적용한다. */
    focus?: { coachId?: string; nonce: string } | null;
    /** 강사 1명을 볼 때 「이 강사 급여 보기」로 같은 정산 화면의 급여 계산 탭을 연다(그 달·그 강사). */
    onOpenSalary?: (coachId: string) => void;
};

type TabKey = SessionReportSheetKey | 'mismatch';

type DisplayRow = SessionReportRow & {
    coachName: string;
    sheetKey: SessionReportSheetKey;
};

const NO_COACH = '';

const GHOST_DAYS_STORAGE_KEY = 'rappo:sessionReport:ghostDays';
const DEFAULT_GHOST_DAYS = 30;

const readGhostDays = () => {
    try {
        const raw = window.localStorage.getItem(GHOST_DAYS_STORAGE_KEY);
        const n = raw === null ? NaN : Number(raw);
        return Number.isInteger(n) && n >= 0 && n <= 365 ? n : DEFAULT_GHOST_DAYS;
    } catch {
        return DEFAULT_GHOST_DAYS;
    }
};

const writeGhostDays = (n: number) => {
    try {
        window.localStorage.setItem(GHOST_DAYS_STORAGE_KEY, String(n));
    } catch {
        // 브라우저 저장이 막혀 있어도 이번 화면에서는 그대로 쓴다.
    }
};

const displayNumber = (n: number) => formatReportNumber(convertToKRW(n));
const won = (n: number | null | undefined) => `${displayNumber(n ?? 0)}원`;
const times = (n: number | null | undefined) => `${displayNumber(n ?? 0)}회`;
const dashIfZero = (n: number | null | undefined, format: (v: number) => string = (v) => displayNumber(v)) =>
    !n ? '-' : format(n);

const WARNING_BG = '#FEF2F2';
const WARNING_BORDER = '#FECACA';
const WARNING_TEXT = '#B91C1C';

const columnHelper = createColumnHelper<DisplayRow>();

/** 탭별 줄 수 — 서버 totals.rowCount / reference.checkNeededRowCount(구서버는 mismatchRowCount) 를 그대로 읽는다. */
const coachTabCount = (coach: SessionReportCoach, tab: TabKey) =>
    tab === 'mismatch' ? screenCheckNeededCount(coach.summary.reference) : coach.sheets[tab]?.totals?.rowCount ?? 0;

// 화면 흐름과 안내가 같은 범위의 요약을 그대로 읽어요. 강사 조회만 성공한 경우도 같아요.
const reportFlow = (report: SessionReport | undefined, selectedCoachId: string) => {
    const coach = selectedCoachId
        ? report?.coaches.find((item) => item.coachId === selectedCoachId)
        : !report?.centerTotals ? report?.coaches[0] : undefined;
    return {
        totals: selectedCoachId ? coach?.summary : report?.centerTotals ?? coach?.summary,
        scope: coach ? `${coach.coachName} 강사` : '센터 전체',
    };
};

const SessionReportPage = ({ centerId, centerName = '', date, onMonthNavigate, coachList, createAlert, focus, onOpenSalary }: Props) => {
    const queryClient = useQueryClient();
    const yearMonth = dayjs(date).format('YYYY-MM');
    const isFutureOrCurrentMonth = !dayjs(date).isBefore(dayjs(), 'month');

    const [ghostDays, setGhostDays] = useState<number>(readGhostDays);
    const [ghostDaysDraft, setGhostDaysDraft] = useState<string>(String(readGhostDays()));
    const ghostDaysInputRef = useRef<HTMLInputElement>(null);
    const openGhostSettings = () => {
        const input = ghostDaysInputRef.current;
        const settings = input?.closest('details');
        if (!settings) return;
        settings.open = true;
        settings.scrollIntoView({ behavior: 'smooth', block: 'start' });
        input?.focus({ preventScroll: true });
    };
    const [selectedCoachId, setSelectedCoachId] = useState<string>(NO_COACH);
    const [coachSelectionNotice, setCoachSelectionNotice] = useState<{ basis: string; text: string } | null>(null);
    const selectedTabBasis = useRef('');
    const [reviewDetailCoachId, setReviewDetailCoachId] = useState<string | null>(null);
    const [tab, setTab] = useState<TabKey>('valid');
    const [search, setSearch] = useState('');
    const guide = useSessionReportGuide();
    const [isNoticeOpen, setIsNoticeOpen] = useReportDisclosure('notices');
    // 진행 상태는 회차 id 로 관리한다(다른 메뉴에 가 있는 동안 끝나도 정리된다).
    const exportRun = useExportRun();
    const exportProgress = exportRun.progress;
    const memberSectionRef = useRef<HTMLDivElement>(null);
    const [memberScrollRequest, setMemberScrollRequest] = useState(0);
    const consumedMemberScroll = useRef(0);
    useEffect(() => {
        if (memberScrollRequest === consumedMemberScroll.current || !memberSectionRef.current) return;
        consumedMemberScroll.current = memberScrollRequest;
        memberSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });

    // 1) 총괄용 요약(회원 행 없음)
    const summaryQuery = useSessionReport({ centerId, yearMonth, ghostDays });
    const summaryResult = summaryQuery.data;
    // 이전 결과(keepPreviousData)는 같은 센터 안에서 유령 기준을 바꿀 때만 보인다.
    // 다른 센터로 바뀌었거나 새 조회가 실패했으면 이전 결과를 보이지 않는다.
    const lastSummaryCenterId = useRef<string | undefined>(undefined);
    if (summaryQuery.isSuccess && !summaryQuery.isPreviousData) lastSummaryCenterId.current = centerId;
    const isStalePrevious =
        summaryQuery.isPreviousData && (summaryQuery.isError || lastSummaryCenterId.current !== centerId);
    const summaryReport =
        summaryResult?.status === 'ok' && summaryResult.report.yearMonth === yearMonth && !isStalePrevious
            ? summaryResult.report
            : undefined;

    // 2) 고른 강사의 회원 행
    const coachQuery = useSessionReport({
        centerId,
        yearMonth,
        ghostDays,
        coachId: selectedCoachId || undefined,
        // 통합 목록 상세가 열려 있으면 그 목록이 해당 강사 하나의 갱신을 맡는다.
        enabled: !!selectedCoachId && !reviewDetailCoachId,
        keepPreviousData: false,
    });
    const coachResult = coachQuery.data;
    const coachReport =
        coachResult?.status === 'ok' && coachResult.report.yearMonth === yearMonth ? coachResult.report : undefined;

    // 요약이 시간 초과면 강사를 지정한 조회를 기준으로 보여 준다(서버 안내: 「강사를 지정해 다시 조회해 주세요」).
    const summaryTimeoutError = isSessionReportTimeout(summaryQuery.error) ? summaryQuery.error : undefined;
    const coachTimeoutError = isSessionReportTimeout(coachQuery.error) ? coachQuery.error : undefined;
    const isSummaryTimeout = !!summaryTimeoutError && !summaryReport;

    // 강사 표와 통합 목록이 같은 상세 응답을 보면 요약 재조회도 함께 한 번만 한다.
    const summarySyncRequests = useRef(new Map<string, ReturnType<typeof summaryQuery.refetch>>());
    const syncSummaryForCoach = (coachId: string, detailUpdatedAt: number) => {
        if (hasResolutionRefresh(queryClient, { centerId: centerId ?? '', coachId, yearMonth, ghostDays }, detailUpdatedAt)) {
            return Promise.resolve(summaryQuery);
        }
        const key = `${centerId}|${yearMonth}|${ghostDays}|${coachId}|${detailUpdatedAt}`;
        const existing = summarySyncRequests.current.get(key);
        if (existing) return existing;
        const request = summaryQuery.refetch({ cancelRefetch: false });
        summarySyncRequests.current.set(key, request);
        return request;
    };

    // 화면 혼합 방지: 강사 상세가 캐시된 요약과 다른 계산이면 요약을 다시 받는다(같은 상세 응답엔 한 번만).
    // 다시 받은 요약이 상세와 같을 때만 「다시 불러왔어요」, 실패하거나 여전히 다르면 경고를 남긴다.
    const syncState = useCoachSummarySync({
        summaryReport,
        coachReport,
        selectedCoachId,
        isFetching: summaryQuery.isFetching || coachQuery.isFetching,
        summaryUpdatedAt: summaryQuery.dataUpdatedAt,
        detailUpdatedAt: coachQuery.dataUpdatedAt,
        refetchSummary: () => syncSummaryForCoach(selectedCoachId, coachQuery.dataUpdatedAt),
        resetKey: `${centerId}|${yearMonth}|${ghostDays}`,
    });
    const baseReport = summaryReport ?? (isSummaryTimeout ? coachReport : undefined);
    const guideFlow = reportFlow(baseReport, selectedCoachId);
    const selectedCoachMissing = !!selectedCoachId && !!baseReport && !baseReport.coaches.some((coach) => coach.coachId === selectedCoachId);
    // 정상 조회 뒤 같은 조건 「다시 계산」이 실패하면 이전 자료가 남는다 — 이전 결과임을 알리고 엑셀을 막는다(11회차 지적 2).
    const refetchFailure = describeSummaryRefetchFailure({
        hasReport: !!summaryReport,
        isError: summaryQuery.isError,
        isFetching: summaryQuery.isFetching,
        error: summaryQuery.error,
        generatedAt: summaryReport?.generatedAt,
        isTimeout: isSessionReportTimeout(summaryQuery.error),
    });
    const isRecalculating = summaryQuery.isFetching && !!summaryReport;

    const applyGhostDays = () => {
        const n = Number(ghostDaysDraft);
        if (!Number.isInteger(n) || n < 0 || n > 365) {
            setGhostDaysDraft(String(ghostDays));
            return;
        }
        if (n !== ghostDays) {
            setGhostDays(n);
            writeGhostDays(n);
        }
    };

    const coachOptions = useMemo(() => {
        if (summaryReport) return summaryReport.coaches.map((c) => ({ code: c.coachId, value: c.coachName }));
        return (coachList ?? []).filter((c) => c.code && c.code !== 'all').map((c) => ({ code: c.code, value: c.value }));
    }, [summaryReport, coachList]);

    // 월을 바꾸거나 시간 초과 후 처음 상세를 받으면 그 달의 첫 탭을 정한다.
    // 같은 달에서 직접 바꾼 탭은 요약 갱신으로 덮어쓰지 않는다.
    useEffect(() => {
        if (summaryReport ? !summaryQuery.isSuccess || summaryQuery.isPreviousData || summaryQuery.isFetching
            : !coachQuery.isSuccess || coachQuery.isFetching) return;
        const coach = baseReport?.coaches.find((item) => item.coachId === selectedCoachId);
        const basis = `${centerId}|${yearMonth}|${selectedCoachId}`;
        if (!coach || selectedTabBasis.current === basis) return;
        selectedTabBasis.current = basis;
        setTab(coachTabCount(coach, 'mismatch') > 0 ? 'mismatch' : 'valid');
        setSearch('');
    }, [baseReport, summaryReport, summaryQuery.isSuccess, summaryQuery.isPreviousData, summaryQuery.isFetching,
        coachQuery.isSuccess, coachQuery.isFetching, selectedCoachId, centerId, yearMonth]);

    // 새 달의 요약을 실제로 받은 뒤에만 해제한다. 로딩·실패·이전 달 캐시는 선택을 지우지 않는다.
    useEffect(() => {
        if (!selectedCoachId || !summaryReport || !summaryQuery.isSuccess || summaryQuery.isPreviousData
            || summaryQuery.isFetching || summaryReport.ghostDays !== ghostDays) return;
        if (summaryReport.coaches.some((coach) => coach.coachId === selectedCoachId)) return;
        setSelectedCoachId(NO_COACH);
        setSearch('');
        setCoachSelectionNotice({ basis: `${centerId}|${yearMonth}`, text: `${Number(yearMonth.split('-')[1])}월에는 선택한 강사 자료가 없어 전체 보기로 바꿨어요.` });
    }, [selectedCoachId, summaryReport, summaryQuery.isSuccess, summaryQuery.isPreviousData, summaryQuery.isFetching, ghostDays, centerId, yearMonth]);

    // 알림 카드로 들어오면 그 강사를 고른다. 첫 탭은 그 달 요약을 받은 뒤 위 effect 가 정하고,
    // 그 달에 강사 자료가 없으면 기존 해제 effect 가 전체 보기로 돌리며 안내한다.
    const appliedFocus = useRef<string | null>(null);
    useEffect(() => {
        if (!focus || appliedFocus.current === focus.nonce) return;
        appliedFocus.current = focus.nonce;
        if (!focus.coachId) return;
        selectedTabBasis.current = '';
        setSelectedCoachId(focus.coachId);
        setSearch('');
        setCoachSelectionNotice(null);
        setMemberScrollRequest((request) => request + 1);
    }, [focus]);

    const selectCoach = (coachId: string, nextTab?: TabKey) => {
        if (coachId) setMemberScrollRequest((request) => request + 1);
        else consumedMemberScroll.current = memberScrollRequest;
        setSelectedCoachId(coachId);
        const coach = baseReport?.coaches.find((item) => item.coachId === coachId);
        const summarySettled = summaryReport ? summaryQuery.isSuccess && !summaryQuery.isPreviousData && !summaryQuery.isFetching
            : coachQuery.isSuccess && !coachQuery.isFetching;
        selectedTabBasis.current = nextTab || (coach && summarySettled) ? `${centerId}|${yearMonth}|${coachId}` : '';
        setTab(nextTab ?? (coach && coachTabCount(coach, 'mismatch') > 0 ? 'mismatch' : 'valid'));
        setSearch('');
        setCoachSelectionNotice(null);
    };

    const recalculate = () => {
        // 합계가 그대로여도 문제 회원은 바뀔 수 있다. 명시적 재계산은 기존 캐시 무효화 기준을 따른다.
        queryClient.invalidateQueries({
            queryKey: sessionReportKeys.report(centerId ?? '', yearMonth, ghostDays).slice(0, -1),
            predicate: (query) => !!query.queryKey[5],
            refetchType: 'none',
        });
        void queryClient.invalidateQueries({ queryKey: supportListKey(centerId ?? '') });
        summaryQuery.refetch();
        if (selectedCoachId && !reviewDetailCoachId) coachQuery.refetch();
    };

    // 엑셀을 막는 이유. 화면에 보이는 값과 파일 값의 기준이 다르면 안 된다.
    // 다시 계산이 실패한 채면 막는다: 화면은 이전 결과인데 파일은 새로 조회해 만드므로 기준이 갈리고, 같은 조건이라 대개 다시 실패한다.
    const exportBlockedReason = !summaryReport
        ? '세션 현황을 불러오는 중이에요'
        : refetchFailure
          ? REFETCH_FAILED_EXPORT_BLOCKED_TEXT
          : summaryQuery.isFetching || summaryQuery.isPreviousData || (!!selectedCoachId && coachQuery.isFetching)
          ? '다시 계산하는 중이에요. 끝나면 받을 수 있어요'
          : syncState === 'mismatch'
            ? '요약과 상세의 계산 시점이 달라요. 「다시 계산」을 누른 뒤 받아 주세요'
          : ghostDaysDraft.trim() !== String(ghostDays) || summaryReport.ghostDays !== ghostDays
            ? '장기 미출석 기준을 바꿨어요. 「다시 계산」을 누른 뒤 받아 주세요'
            : undefined;

    // 엑셀 수집 중 화면을 떠나면(언마운트·로그아웃·KeepAlive 로 다른 메뉴 이동) 취소하고 알림을 띄우지 않는다.
    // 센터가 바뀌면 취소하고 「바뀌어서 만들지 않았어요」만 알린다(이전 강사명 없이).
    const exportAbort = useRef<AbortController | null>(null);
    const isActive = useRef(true);
    useEffect(() => {
        isActive.current = true;
        return () => {
            isActive.current = false;
            exportAbort.current?.abort();
        };
    }, []);
    // KeepAlive: 다른 메뉴로 가면 언마운트 없이 비활성화된다. 그때도 취소한다.
    useEffectOnActive(() => {
        isActive.current = true;
        return () => {
            isActive.current = false;
            exportAbort.current?.abort();
        };
    }, []);
    useEffect(() => () => exportAbort.current?.abort(PRINCIPAL_CHANGED_ABORT), [centerId]);
    const readPrincipal = () => exportPrincipalKey(getItemWithExpiration('authObject'));

    const exportExcel = async () => {
        if (!summaryReport || !centerId || exportProgress || exportBlockedReason) return;
        // 누른 순간의 기준으로 고정한다. 받는 동안 입력을 바꿔도 이 값으로만 받는다.
        const basis = { centerId, yearMonth, ghostDays };
        const principalAtStart = readPrincipal();
        const controller = new AbortController();
        exportAbort.current = controller;
        const runId = exportRun.begin(summaryReport.coaches.length);
        let result: Awaited<ReturnType<typeof collectConsistentExport>>;
        try {
            result = await collectConsistentExport({
                yearMonth: basis.yearMonth,
                ghostDays: basis.ghostDays,
                fetchSummary: () => fetchSessionReportFresh(queryClient, basis),
                fetchCoach: (coachId) => fetchSessionReportFresh(queryClient, { ...basis, coachId }),
                onProgress: (done, total) => !controller.signal.aborted && exportRun.update(runId, done, total),
                signal: controller.signal,
            });
        } finally {
            if (exportAbort.current === controller) exportAbort.current = null;
            // 취소·완료·실패 모두 이 회차의 진행 표시는 활성 여부와 무관하게 지운다(알림만 활성일 때).
            exportRun.finish(runId);
        }
        // 토큰이 아니라 로그인 주체(센터 + 관리자)로 판정한다 — 같은 로그인의 토큰 갱신은 그대로 저장한다.
        const outcome = decideExportOutcome({
            aborted: controller.signal.aborted,
            abortReason: controller.signal.reason,
            active: isActive.current,
            startPrincipal: principalAtStart,
            currentPrincipal: readPrincipal(),
        });
        if (outcome === 'silent') return;
        if (outcome === 'principalChanged') {
            createAlert?.('엑셀을 만들지 않았어요', '받는 사이 로그인한 계정이나 센터가 바뀌었어요. 다시 받아 주세요.', true);
            return;
        }
        if (result.status === 'aborted') return;
        if (result.status === 'coachFailed') {
            createAlert?.(
                '엑셀을 만들지 못했어요',
                `${result.failed.map((f) => `${f.coachName}(${f.reason})`).join(', ')} 강사의 회원 자료를 불러오지 못했어요. 강사가 빠진 파일을 드리지 않으려고 만들지 않았어요. 잠시 후 다시 받아 주세요.`,
                true,
            );
            return;
        }
        if (result.status === 'summaryFailed') {
            createAlert?.(
                '엑셀을 만들지 못했어요',
                result.reason === '시간 초과'
                    ? SUMMARY_TIMEOUT_EXPORT_TEXT
                    : result.reason === SESSION_REPORT_TOO_MANY_REASON
                      ? SESSION_REPORT_TOO_MANY_MESSAGE
                      : `센터 요약을 다시 불러오지 못했어요(${result.reason}). 잠시 후 다시 받아 주세요.`,
                true,
            );
            return;
        }
        if (result.status === 'changed') {
            createAlert?.('엑셀을 만들지 못했어요', '그 사이 데이터가 바뀌었어요. 다시 받아 주세요.', true);
            return;
        }
        const workbook = buildSessionReportWorkbook(result.summary, result.reports, centerName);
        const buffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        saveAs(
            new Blob([buffer], { type: 'application/octet-stream' }),
            buildSessionReportFileName(result.summary, centerName),
        );
    };

    const header = (
        <Row className="items-center flex-wrap gap-[10px]">
            <div role="group" aria-label="조회 월 선택" className="inline-flex min-h-[44px] items-center gap-[8px] rounded-[10px] border border-solid border-[#E1E6ED] bg-white p-[3px] text-[16px] font-[650] text-[#202936]">
                <button type="button" aria-label="이전 달" className="flex h-[38px] w-[38px] items-center justify-center rounded-[8px] text-[#4E5968] hover:bg-[#F5F7FA]"
                    onClick={() => onMonthNavigate(dayjs(date).subtract(1, 'month'))}>
                    <ReportIcon name="right" className="rotate-180" />
                </button>
                <span aria-live="polite" className="min-w-[110px] text-center">{dayjs(date).format('YYYY년 M월')}</span>
                <button type="button" aria-label="다음 달" disabled={isFutureOrCurrentMonth}
                    className="flex h-[38px] w-[38px] items-center justify-center rounded-[8px] text-[#4E5968] hover:bg-[#F5F7FA] disabled:cursor-not-allowed disabled:opacity-40"
                    onClick={() => !isFutureOrCurrentMonth && onMonthNavigate(dayjs(date).add(1, 'month'))}>
                    <ReportIcon name="right" />
                </button>
            </div>
            {coachOptions.length > 0 && (summaryReport || isSummaryTimeout) && <label className="relative flex h-[44px] items-center rounded-[10px] border border-solid border-[#E1E6ED] bg-white shadow-[0_1px_2px_#25334a03]">
                <select aria-label="강사 필터" value={selectedCoachId || 'all'} onChange={(event) => {
                    const next = event.target.value === 'all' ? NO_COACH : event.target.value;
                    if (next !== selectedCoachId) selectCoach(next);
                }} className="h-full w-[160px] appearance-none bg-transparent pl-[15px] pr-[35px] text-[14px] font-medium text-[#202936]">
                    <option value="all">강사 전체</option>
                    {coachOptions.filter((option) => option.code !== 'all').map((option) => <option key={option.code} value={option.code}>{option.value}</option>)}
                </select>
                <ReportIcon name="down" className="pointer-events-none absolute right-[12px] !w-[14px] text-[#4E5968]" />
            </label>}
        </Row>
    );
    const ghostControls = (<>
            <Row className="items-center flex-wrap gap-[10px] mt-[16px]">
                <Row className="items-center bg-[#F2F4F6] rounded-[7px] px-[12px] h-[40px]">
                    <RegularText fontSize={14} lineHeight={20} color="#4E5968" className="mr-[6px]">
                        안 온 기간 기준
                    </RegularText>
                    <input
                        ref={ghostDaysInputRef}
                        aria-label="장기 미출석 기준 일수"
                        inputMode="numeric"
                        value={ghostDaysDraft}
                        maxLength={3}
                        onChange={(e) => setGhostDaysDraft(e.target.value.replace(/[^0-9]/g, ''))}
                        onBlur={applyGhostDays}
                        onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
                        className="w-[36px] h-[24px] text-center bg-white rounded-[4px] text-[14px] text-[#3D4244] outline-none border border-solid border-[#E4E4E4]"
                    />
                    <RegularText fontSize={14} lineHeight={20} color="#4E5968" className="ml-[4px]">
                        일
                    </RegularText>
                    <TooltipIcon
                        title={'기간이 지났는데 회차가 남은 수강권 중, 마지막 출석(출석이 없으면 시작일) 뒤 이 일수가 지난 회원을 「며칠 넘게 안 옴」으로 모아요. 엑셀에서는 「유령」 시트예요.'}
                        ml={6}
                        iconSize={14}
                        width={240}
                    />
                </Row>
            </Row>
    </>);
    const controls = <><button type="button" onClick={(event) => {
        const menu = event.currentTarget.closest('details');
        if (menu) {
            menu.open = false;
            menu.querySelector('summary')?.focus();
        }
        guide.show();
    }} className="min-h-[40px] text-[14px] text-[#3D4244]">화면 안내</button>{ghostControls}</>;

    let body: React.ReactNode;
    if (summaryResult?.status === 'unavailable') {
        body = (
            <StateMessage
                title="아직 준비 중이에요"
                description="서버 업데이트 후 사용할 수 있어요. 기존 정산 화면은 그대로 쓰실 수 있어요."
            />
        );
    } else if (summaryResult?.status === 'futureMonth' && !summaryQuery.isFetching) {
        body = (
            <StateMessage
                title="아직 시작하지 않은 달이에요"
                description="지난 달이나 이번 달을 선택해 주세요."
                actionText="이번 달 보기"
                onAction={() => onMonthNavigate(dayjs())}
            />
        );
    } else if (isSummaryTimeout && !baseReport && !summaryQuery.isFetching) {
        body = selectedCoachId && coachQuery.isFetching ? (
            <LoadingSkeleton />
        ) : (
            <StateMessage
                title="센터 전체를 한 번에 정리하지 못했어요"
                description={(selectedCoachId && coachTimeoutError?.message) || summaryTimeoutError?.message}
                actionText="다시 시도"
                onAction={recalculate}
            >
                {coachOptions.length > 0 && (
                    <Col className="items-center mt-[28px]">
                        <RegularText fontSize={14} lineHeight={20} color="#4E5968" className="mb-[10px]">
                            강사를 고르면 그 강사 표부터 볼 수 있어요
                        </RegularText>
                        <Row className="items-center justify-center flex-wrap gap-[8px] max-w-[900px]">
                            {coachOptions.map((c) => (
                                <Row
                                    key={c.code}
                                    className="items-center h-[34px] px-[14px] rounded-[17px] cursor-pointer border border-solid border-[#E4E4E4] bg-white"
                                    onClick={() => selectCoach(c.code)}
                                >
                                    <RegularText fontSize={14} lineHeight={20} color="#3D4244">
                                        {c.value}
                                    </RegularText>
                                </Row>
                            ))}
                        </Row>
                    </Col>
                )}
            </StateMessage>
        );
    } else if (summaryQuery.isError && !summaryQuery.isFetching && !baseReport) {
        body = (
            <StateMessage
                title="세션 현황을 불러오지 못했어요"
                description="잠시 후 다시 시도해 주세요. 계속되면 고객센터로 알려 주세요."
                actionText="다시 시도"
                onAction={() => summaryQuery.refetch()}
            />
        );
    } else if (!baseReport) {
        body = <LoadingSkeleton />;
    } else if (selectedCoachMissing) {
        // 새 달의 선택 유효성을 확인 중이다. 강사 자료 없음이 확정되기 전에는 0회·손볼 회원 없음으로 보이지 않는다.
        body = summaryQuery.isError && !summaryQuery.isFetching
            ? <StateMessage title="선택한 강사 자료를 확인하지 못했어요" description="잠시 후 다시 시도해 주세요." actionText="다시 시도" onAction={() => summaryQuery.refetch()} />
            : <LoadingSkeleton />;
    } else {
        body = (
            <ReportBody
                header={header}
                controls={controls}
                onOpenGhostSettings={openGhostSettings}
                centerId={centerId}
                report={baseReport}
                isPartial={!summaryReport}
                selectedCoachId={selectedCoachId}
                selectionNotice={coachSelectionNotice?.basis === `${centerId}|${yearMonth}` ? coachSelectionNotice.text : undefined}
                coachState={{
                    report: coachReport,
                    isLoading: !!selectedCoachId && coachQuery.isFetching && !coachReport,
                    isError: !!selectedCoachId && !coachQuery.isFetching && (coachQuery.isError || (!!coachResult && coachResult.status !== 'ok')),
                    errorText: coachTimeoutError?.message,
                    retry: () => coachQuery.refetch(),
                }}
                tab={tab}
                search={search}
                isRecalculating={isRecalculating}
                isNoticeOpen={isNoticeOpen}
                exportProgress={exportProgress}
                exportBlockedReason={exportBlockedReason}
                syncState={syncState}
                refetchFailure={refetchFailure ?? undefined}
                onRecalculate={recalculate}
                onSyncSummary={syncSummaryForCoach}
                onReviewDetailChange={setReviewDetailCoachId}
                memberSectionRef={memberSectionRef}
                onToggleNotice={() => setIsNoticeOpen((v) => !v)}
                onTabChange={(nextTab) => {
                    selectedTabBasis.current = `${centerId}|${yearMonth}|${selectedCoachId}`;
                    setTab(nextTab);
                }}
                onSearch={setSearch}
                onSelectCoach={selectCoach}
                onExport={exportExcel}
                onPrevMonth={() => onMonthNavigate(dayjs(date).subtract(1, 'month'))}
                // 급여 계산 탭의 강사 고르기는 정산 화면 강사 목록을 쓴다. 그 목록에 없는 강사면 링크를 숨긴다.
                onOpenSalary={onOpenSalary && selectedCoachId && coachList?.some((coach) => coach.code === selectedCoachId)
                    ? () => onOpenSalary(selectedCoachId) : undefined}
            />
        );
    }

    return (
        <div className="session-report flex flex-col h-full">
            {(!baseReport || selectedCoachMissing) && <div className="mb-[24px] flex items-start justify-between gap-[20px]">{header}<SessionReportOptions controls={controls} /></div>}
            {body}
            {guide.open && (
                <SessionReportGuide
                    onClose={guide.close}
                    example={guideFlow.totals ?? null}
                    scope={guideFlow.scope}
                    ghostDays={ghostDays}
                    hidden={guide.hidden}
                />
            )}
        </div>
    );
};

export default SessionReportPage;

// ───────────────────────────── 본문 ─────────────────────────────

const SUMMARY_TIMEOUT_EXPORT_TEXT = '센터 전체 계산이 오래 걸려 지금은 엑셀을 만들 수 없어요. 잠시 후 다시 시도해 주세요';

export const ReportBody = ({
    header,
    controls,
    onOpenGhostSettings,
    centerId = '',
    report,
    isPartial,
    selectedCoachId,
    selectionNotice,
    coachState,
    tab,
    search,
    isRecalculating,
    isNoticeOpen,
    exportProgress,
    exportBlockedReason,
    syncState = 'none',
    refetchFailure,
    onRecalculate,
    onSyncSummary,
    onReviewDetailChange,
    memberSectionRef,
    onToggleNotice,
    onTabChange,
    onSearch,
    onSelectCoach,
    onExport,
    onPrevMonth,
    onOpenSalary,
}: {
    header?: React.ReactNode;
    controls?: React.ReactNode;
    onOpenGhostSettings?: () => void;
    /** 수업 옮기기(「수업 보기」)에 쓴다. 없으면 「수업 보기」를 보이지 않는다. */
    centerId?: string;
    report: SessionReport;
    /** 요약이 시간 초과라 강사 한 명 조회로 대신 보이는 중 */
    isPartial: boolean;
    selectedCoachId: string;
    selectionNotice?: string;
    coachState: {
        report?: SessionReport;
        isLoading: boolean;
        isError: boolean;
        errorText?: string;
        retry: () => void;
    };
    tab: TabKey;
    search: string;
    isRecalculating: boolean;
    isNoticeOpen: boolean;
    exportProgress: { done: number; total: number } | null;
    /** 있으면 엑셀 버튼을 막고 이 이유를 보인다(다시 계산 중·기준 불일치) */
    exportBlockedReason?: string;
    /** 강사 상세와 요약의 계산 시점 대조 결과 */
    syncState?: 'none' | 'refreshed' | 'mismatch';
    /** 같은 조건 다시 계산이 실패해 이전 결과를 보이는 중(describeSummaryRefetchFailure) */
    refetchFailure?: { title: string; detail: string };
    onRecalculate?: () => void;
    onSyncSummary?: (coachId: string, detailUpdatedAt: number) => Promise<unknown>;
    onReviewDetailChange?: (coachId: string | null) => void;
    memberSectionRef: React.RefObject<HTMLDivElement>;
    onToggleNotice: () => void;
    onTabChange: (tab: TabKey) => void;
    onSearch: (search: string) => void;
    onSelectCoach: (coachId: string, tab?: TabKey) => void;
    onExport: () => void;
    onPrevMonth: () => void;
    /** 강사 1명을 볼 때만 의미가 있다. 강사 전체 보기에서는 넘겨도 링크를 숨긴다. */
    onOpenSalary?: () => void;
}) => {
    const supportList = useSupportList(centerId, report.yearMonth);
    const supportGuidance = getSupportGuidance(supportList.view);
    const month = Number(report.yearMonth.split('-')[1]);
    // 지난 달을 볼 때 「이번 달」「지금」이라고 하면 오늘 값으로 읽힌다 — 진행 중인 달만 그렇게 부른다.
    // 서버의 isMidMonth 는 계산 시점 값이다. 탭을 열어 둔 채 달이 바뀌면 지난달이 되므로 오늘 달과도 맞춰 본다.
    const isCurrentMonth = report.isMidMonth && report.yearMonth === formatKst(new Date().toISOString(), 'YYYY-MM');
    const monthWord = isCurrentMonth ? '이번 달' : `${month}월`;
    // 월 중간에 센 값인데 달이 바뀌었으면 「N월 말」이 아니라 센 날짜로 부른다.
    const atWord = isCurrentMonth ? '지금' : report.isMidMonth ? `${formatKst(report.cutoffAt, 'M월 D일 HH:mm')}` : `${month}월 말`;
    // 강사를 고르거나 요약 시간 초과로 한 명 자료만 받으면 숫자의 범위를 이름으로 명시한다.
    const selectedCoach = report.coaches.find((c) => c.coachId === selectedCoachId);
    const scopeWord = selectedCoach ? `${selectedCoach.coachName} 강사 ` : report.centerTotals ? '' : `${report.coaches[0]?.coachName ?? ''} 강사 `;
    const scopedCoaches = selectedCoachId ? report.coaches.filter((coach) => coach.coachId === selectedCoachId) : report.coaches;
    const monthWords = useMemo(() => ({ month: monthWord, at: atWord }), [monthWord, atWord]);
    // IN·OUT 열은 보고서에 OUT 이 있을 때만(일반 센터는 IN = 진행이라 같은 숫자 반복). 요약·회원별 표가 같은 판정을 쓴다.
    const showInOut = hasInOutSplit(report);

    // 표시 범위와 다른 강사로 건너갈 때 비교할 센터 전체 범위를 구분한다.
    const countAll = (key: TabKey) => scopedCoaches.reduce((acc, c) => acc + coachTabCount(c, key), 0);
    const countCenter = (key: TabKey) => report.coaches.reduce((acc, c) => acc + coachTabCount(c, key), 0);
    const totalRows = report.coaches.reduce((acc, coach) => acc + SESSION_REPORT_SHEETS.reduce((sum, { key }) => sum + coachTabCount(coach, key), 0), 0);
    // 강사 한 명만 보이는 중(isPartial)이면 줄 수도 그 강사 기준으로 맞춘다(전체 n줄 중 m줄의 분모·분자가 같은 범위여야 한다).
    const mismatchTotal = selectedCoachId || isPartial
        ? countAll('mismatch')
        : report.centerTotals
          ? screenCheckNeededCount(report.centerTotals.reference)
          : countAll('mismatch');
    // 확인할 내용 종류별(서버 checkFlagCounts). 검산 차이는 플래그가 아니라 mismatchRowCount 로 따로 붙인다.
    const flagSource = selectedCoachId ? selectedCoach?.summary.reference : report.centerTotals?.reference ?? (isPartial ? report.coaches[0]?.summary.reference : undefined);
    const checkFlags = [
        ...(mismatchCount(flagSource) > 0
            ? [{ key: '__mismatch', label: '계산이 안 맞음', count: mismatchCount(flagSource) }]
            : []),
        ...listCheckFlags(flagSource).map((f) => ({ key: f.flag, label: f.label, count: f.count })),
        // 플래그 집계(checkFlagCounts)는 없을 수 있다. 미수 계산 불가 행 수는 따로 오므로, 집계에 없으면 채워
        // 강사 배지(topReviewLevel)와 확인할 일 상자가 「문의할 것」을 다르게 말하지 않게 한다.
        ...(!flagSource?.checkFlagCounts?.UNPAID_UNKNOWN && unpaidReviewCount(flagSource) > 0
            ? [{ key: 'UNPAID_UNKNOWN', label: describeFlagShort('UNPAID_UNKNOWN'), count: unpaidReviewCount(flagSource) }]
            : []),
        // 안내용(할 일 없음) 플래그는 확인할 일 건수에서 빠지고 「할 일 없는 안내」에만 보인다. 옛 인계는 원래대로 숨긴다.
        ...Array.from(INFO_ONLY_FLAGS).filter((key) => key !== 'UNLINKED_HANDOFF').flatMap((key) => {
            const count = checkFlagCounts(flagSource)[key] ?? 0;
            return count > 0 ? [{ key, label: reviewReasonLabel(key), count }] : [];
        }),
    ];
    const reviewLevels = REVIEW_LEVEL_ORDER.filter((level) => level !== 'info');
    const reasonCounts = (level: (typeof REVIEW_LEVEL_ORDER)[number]) => checkFlags
        .filter((f) => flagReviewLevel(f.key) === level).reduce((sum, f) => sum + f.count, 0);
    const reviewSummary = reviewLevels.filter((level) => reasonCounts(level) > 0)
        .map((level) => `${REVIEW_TONES[level].label} ${displayNumber(reasonCounts(level))}건`).join(' · ');
    const { totals: overview, scope: flowScope } = reportFlow(report, selectedCoachId);
    const [showTableDetail, setShowTableDetail] = useReportDisclosure('tableDetail');
    const [showReview, setShowReview] = useState(false);
    const [resolvedRequest, setResolvedRequest] = useState(0);
    const openResolvedMembers = () => { setEvidence(null); setResolvedRequest((value) => value + 1); setShowReview(true); };
    const [reviewDetailOpen, setReviewDetailOpen] = useState(false);
    const handleReviewDetailChange = useCallback((coachId: string | null) => {
        setReviewDetailOpen(!!coachId);
        onReviewDetailChange?.(coachId);
    }, [onReviewDetailChange]);
    const [reviewMembers, setReviewMembers] = useState<{ basis: string; count: number; resolved: number; fix: number; ask: number; submitted?: number; ready: boolean; reasons: Record<string, number> } | null>(null);
    const [coachSearch, setCoachSearch] = useState('');
    const [onlyReview, setOnlyReview] = useState(false);
    const reviewBasis = `${centerId}|${report.yearMonth}|${report.ghostDays}`;
    const evidenceBasis = `${reviewBasis}|${selectedCoachId}`;
    const resolvedCount = flagSource?.resolvedCheckNeededRowCount ?? scopedCoaches.reduce((sum, coach) => sum + (coach.summary.reference.resolvedCheckNeededRowCount ?? 0), 0);
    const [evidence, setEvidence] = useState<{ basis: string; selection: FlowEvidenceSelection } | null>(null);
    useEffect(() => { setEvidence(null); setResolvedRequest(0); }, [evidenceBasis]);
    // 서버 목록은 새 플래그를 __unknown으로 묶는다. 실제 행도 같은 키로 대조한다.
    const { __mismatch: listedMismatch = 0, ...listedFlags } = reviewMembers?.reasons ?? {};
    const listedReasons: Record<string, number> = {
        __mismatch: listedMismatch,
        ...Object.fromEntries(listCheckFlags({ checkFlagCounts: listedFlags }).map(({ flag, count }) => [flag, count])),
    };
    // 목록 인원과 회원 행 밖의 확인 기록은 다른 단위다. 서버에만 남은 사유를 정상으로 덮지 않는다.
    const unlistedChecks = reviewMembers?.basis === `${reviewBasis}|${selectedCoachId}` && reviewMembers.ready
        ? checkFlags.filter((item) => flagReviewLevel(item.key) !== 'info' && item.count > (listedReasons[item.key] ?? 0)) : [];
    const unlistedSummary = unlistedChecks.map((item) => `${describeReviewIssue(item.key)} ${displayNumber(item.count - (listedReasons[item.key] ?? 0))}건`).join(' · ');
    useEffect(() => { setCoachSearch(''); setOnlyReview(false); }, [reviewBasis]);
    const [reasonFilter, setReasonFilter] = useState<{ key: string; coachId: string } | null>(null);
    const [showExplanations, setShowExplanations] = useReportDisclosure('explanations');
    const coachSectionRef = useRef<HTMLDivElement>(null);
    const firstCoachWith = (key: TabKey) => selectedCoachId || report.coaches.find((c) => coachTabCount(c, key) > 0)?.coachId;

    // 회원 상세는 이 화면 안에서만 그린다(전역 오버레이 금지 — 화면을 떠나거나 로그인 화면으로 가면 함께 사라져야 한다).
    // 행 객체가 아니라 rowKey 만 들고, 지금 받은 자료에서 찾는다 — 강사·센터·달이 바뀌면 행이 없어 자동으로 닫힌다.
    const [detailRowKey, setDetailRowKey] = useState<string | null>(null);
    // 회원 상세에서 연 수강권 정보 창(수강권 id). 회원 상세와 같은 규칙으로 화면을 떠나면 닫힌다.
    const [openMembership, setOpenMembership] = useState<{ membershipId: string; coachName: string; memberName: string } | null>(
        null,
    );
    useEffectOnActive(
        () => () => {
            setDetailRowKey(null);
            setOpenMembership(null);
            setShowReview(false);
            setEvidence(null);
        },
        [],
    );
    const coachDetail = pickCoachFromReport(coachState.report, selectedCoachId);

    // 고른 강사의 회원 행(무제한권 행은 rowKey 로 따로 온다 — 이름으로 합치지 않는다)
    const coachRows = useMemo<DisplayRow[]>(() => {
        if (!coachDetail) return [];
        const out: DisplayRow[] = [];
        for (const { key } of SESSION_REPORT_SHEETS) {
            for (const row of coachDetail.sheets[key]?.rows ?? []) {
                out.push({ ...row, coachName: coachDetail.coachName, sheetKey: key });
            }
        }
        return out;
    }, [coachDetail]);
    const detailRow = detailRowKey ? coachRows.find((r) => r.rowKey === detailRowKey) : undefined;
    const tabRows = tab === 'mismatch' ? coachRows.filter(isCheckNeededRow) : coachRows.filter((r) => r.sheetKey === tab);
    // 회원 행을 받은 강사의 확인할 일 탭 숫자는 표와 같은 기준으로 직접 센다 — 서버 건수 기준이 화면과 잠시 어긋나도(배포 사이) 탭과 표가 맞는다.
    const selectedTabCount = (coach: SessionReportCoach, key: TabKey) =>
        key === 'mismatch' && coachDetail && coachRows.length > 0 ? coachRows.filter(isCheckNeededRow).length : coachTabCount(coach, key);
    const keyword = search.trim();
    const activeReason = tab === 'mismatch' && reasonFilter?.coachId === selectedCoachId ? reasonFilter.key : null;
    const reasonRows = activeReason ? tabRows.filter((row) => rowHasReviewReason(row, activeReason)) : tabRows;
    const visibleRows = keyword ? reasonRows.filter((r) => r.memberName.includes(keyword)) : reasonRows;
    const coachesForReason = (key: string) => report.coaches.filter((coach) => coachTabCount(coach, 'mismatch') > 0
        && (key === '__mismatch' ? mismatchCount(coach.summary.reference) > 0 : coachHasCheckFlag(coach.summary.reference, key)));
    const selectReason = (key: string, coachId: string) => {
        setShowReview(false);
        setReasonFilter({ key, coachId });
        onSelectCoach(coachId, 'mismatch');
    };
    useEffect(() => { setReasonFilter(null); }, [report.yearMonth, centerId]);
    useEffect(() => { setReasonFilter((current) => current?.coachId === selectedCoachId ? current : null); }, [selectedCoachId]);

    const memberSearchData = useMemo(
        () => Array.from(new Set(coachRows.map((r) => r.memberName))).map((name) => ({ code: name, value: name })),
        [coachRows],
    );

    if (report.coaches.length === 0) {
        // 빈 보고서여도 다시 계산 실패는 알린다(12회차) — 「없어요」가 이전 결과일 수 있다.
        return (
            <Col className="w-full">
                <div className="mb-[24px] flex items-start justify-between gap-[20px]">
                    {header}
                    <div className="flex items-center gap-[12px]">
                        <button type="button" disabled={isRecalculating} onClick={onRecalculate} className="text-[14px] text-[#2B5DB3]">{isRecalculating ? '계산 중…' : '다시 계산'}</button>
                        <SessionReportOptions controls={controls} />
                    </div>
                </div>
                {selectionNotice && <p role="status" className="mb-[16px] text-[14px] text-[#4E5968]">{selectionNotice}</p>}
                <SessionReportWarnings report={report} isNoticeOpen={isNoticeOpen} onToggleNotice={onToggleNotice} onRecalculate={onRecalculate} isRecalculating={isRecalculating} />
                {refetchFailure && <RefetchFailureBanner failure={refetchFailure} onRecalculate={onRecalculate} />}
                <StateMessage
                    title={`${month}월에 정리할 개인 수업 수강권이 없어요`}
                    description="이 달에 등록·수업·남은 회차가 있는 개인 수업 수강권이 생기면 여기에 자동으로 정리돼요."
                    actionText="지난 달 보기"
                    onAction={onPrevMonth}
                />
            </Col>
        );
    }

    const tabTitle = (key: TabKey) =>
        key === 'mismatch' ? '확인할 일' : key === 'ghost' ? `${report.ghostDays}일 넘게 안 옴` : key === 'valid' ? '기간 안' : '기간 지남';

    return (
        <SupportContext.Provider value={supportList.view}>
        <ReportMonthContext.Provider value={monthWords}>
        <ReportExplanationContext.Provider value={{ expanded: showExplanations, onToggle: () => setShowExplanations((v) => !v) }}>
        <Col className={`w-full text-[#202936] transition-opacity ${isRecalculating ? 'opacity-60' : ''}`}>
            <div className="mb-[22px] flex flex-wrap items-center justify-between gap-[16px]">
                {header ?? <h1 className="text-[24px] leading-[32px] font-noto-medium text-[#3D4244]">{report.yearMonth.replace('-', '년 ')}월</h1>}
                <div className="ml-auto flex items-start gap-[10px]">
                    {isPartial ? <p className="max-w-[300px] text-[14px] leading-[22px] text-[#4E5968]">{SUMMARY_TIMEOUT_EXPORT_TEXT}</p> :
                        <div className="flex max-w-[360px] flex-col items-end gap-[8px]">
                            <div className="flex items-center gap-[8px]">
                                <button type="button" onClick={onExport} disabled={!!exportProgress || !!exportBlockedReason} title="센터 전체 현황을 엑셀로 받아요"
                                    className="flex min-h-[44px] items-center justify-center gap-[9px] rounded-[10px] bg-[#3D73DD] px-[17px] text-[14px] font-[650] leading-[22px] text-white shadow-[0_2px_4px_#3D73DD26] hover:bg-[#2D62C9] disabled:cursor-not-allowed disabled:opacity-40">
                                    <ReportIcon name="download" />
                                    {exportProgress ? `강사별 자료를 모으고 있어요 ${exportProgress.done}/${exportProgress.total}` : '엑셀 받기'}
                                </button>
                            </div>
                            {(exportProgress || exportBlockedReason) && <p className="text-right text-[14px] leading-[22px] text-[#4E5968]">
                                {exportProgress ? '강사마다 회원 자료를 불러오고 있어요. 창을 닫지 말아 주세요.' : exportBlockedReason}
                            </p>}
                        </div>}
                    <SessionReportOptions report={report} totalRows={totalRows} controls={controls} />
                </div>
            </div>
            {selectedCoach && <div role="status" className="mb-[20px] flex flex-wrap items-center gap-[8px] rounded-[10px] bg-[#EDF3FF] px-[16px] py-[10px] text-[15px] text-[#2B529D]">
                <span>{selectedCoach.coachName} 강사만 보는 중</span><span aria-hidden="true">·</span>
                <button type="button" onClick={() => onSelectCoach(NO_COACH)} className="min-h-[40px] font-medium underline underline-offset-4">전체 보기</button>
            </div>}
            {selectionNotice && <p role="status" className="mb-[16px] text-[14px] text-[#4E5968]">{selectionNotice}</p>}
            {refetchFailure && <RefetchFailureBanner failure={refetchFailure} onRecalculate={onRecalculate} />}
            {syncState === 'mismatch' && !isPartial && (
                <Row className="items-center mb-[16px] px-[16px] py-[12px] border border-solid border-WARNING bg-WARNING_50 rounded-[10px] gap-[8px]">
                    <SvgIcon name="SvgWarningInCircle" size={17} fill={colors.WARNING} />
                    <RegularText fontSize={14} lineHeight={20} color={colors.WARNING}>
                        계산 시점이 달라 요약과 상세 숫자가 다를 수 있어요
                    </RegularText>
                    {onRecalculate && (
                        <span
                            role="button"
                            tabIndex={0}
                            className="cursor-pointer underline underline-offset-2"
                            onClick={onRecalculate}
                            onKeyDown={(e) => e.key === 'Enter' && onRecalculate()}
                        >
                            <RegularText fontSize={14} lineHeight={20} color={colors.WARNING}>
                                다시 계산
                            </RegularText>
                        </span>
                    )}
                </Row>
            )}
            {syncState === 'refreshed' && !isPartial && (
                <Row className="items-center mb-[16px] px-[16px] py-[12px] bg-[#F4F7FF] rounded-[10px] gap-[8px]">
                    <SvgIcon name="SvgCheck" size={17} fill={colors.primary} />
                    <RegularText fontSize={14} lineHeight={20} color={colors.primary}>
                        최신 계산으로 다시 불러왔어요
                    </RegularText>
                </Row>
            )}
            {isPartial && (
                <Row className="items-center mb-[16px] px-[16px] py-[12px] border border-solid border-WARNING bg-WARNING_50 rounded-[10px] gap-[8px]">
                    <SvgIcon name="SvgWarningInCircle" size={17} fill={colors.WARNING} />
                    <RegularText fontSize={14} lineHeight={20} color={colors.WARNING}>
                        {!selectedCoach && report.centerTotals
                            ? '센터 전체를 한 번에 정리하지 못해 고른 강사만 보여 드려요. 합계는 센터 전체 값이에요.'
                            : '센터 전체를 한 번에 정리하지 못해 고른 강사만 보여 드려요. 합계는 이 강사 값이에요.'}
                    </RegularText>
                </Row>
            )}
            <section aria-label="세션 현황 요약" className="w-full">
                <h1 className="sr-only">{`${scopeWord}${monthWord} 세션 현황`}</h1>
                {overview && <SessionFlowHero totals={overview} report={report} showInOut={showInOut}
                    resolvedCount={resolvedCount} onOpenResolved={openResolvedMembers}
                    onOpenEvidence={(selection) => { setShowReview(false); setEvidence({ basis: evidenceBasis, selection }); }}
                    onOpenReview={() => { setEvidence(null); setShowReview(true); }} onRecalculate={onRecalculate} isRecalculating={isRecalculating}
                    onOpenSalary={selectedCoach ? onOpenSalary : undefined}
                    scope={flowScope} />}
            </section>
            <SessionReportWarnings report={report} isNoticeOpen={isNoticeOpen} onToggleNotice={onToggleNotice} isCurrentMonth={isCurrentMonth} onPrevMonth={onPrevMonth} onRecalculate={onRecalculate} isRecalculating={isRecalculating} />
            {supportList.isError && <p role="alert" className="mt-[16px] text-[14px] leading-[22px] text-[#4E5968]">
                라포 접수 현황을 불러오지 못했어요. {supportList.error instanceof Error ? supportList.error.message : '잠시 뒤 다시 해 주세요'}
                <button type="button" onClick={() => void supportList.refetch()} className="ml-[8px] min-h-[40px] underline">접수 현황 다시 보기</button>
            </p>}
            {supportList.data?.status === 'ok' && supportList.data.data.truncated && <p className="mt-[12px] text-[14px] text-[#4E5968]">접수가 많아 일부만 보여 드려요. 접수 표시가 없어도 다시 알리면 기존 접수를 확인해 드려요.</p>}
            <section aria-label="확인할 일" className="mt-[22px]">
                {mismatchTotal > 0 || reviewSummary ?
                    <button type="button" aria-label={mismatchTotal > 0 ? `손볼 회원 ${mismatchTotal}건 확인 필요, 보기` : '확인할 기록이 있어요, 보기'} aria-haspopup="dialog" aria-expanded={showReview} onClick={() => setShowReview(true)} className="flex min-h-[52px] w-full items-center gap-[10px] rounded-[12px] border border-solid border-[#EDE6CD] bg-[#FCF9EE] px-[17px] py-[11px] text-left text-[14px] text-[#695930] hover:bg-[#F8F3E2]">
                        <ReportIcon name="alert" className="!h-[19px] !w-[19px] text-[#9B7B36]" />
                        <strong className="font-[650]">{mismatchTotal > 0 ? `손볼 회원 ${mismatchTotal}건 확인 필요` : '확인할 기록이 있어요'}</strong>
                        {reviewMembers?.basis === `${reviewBasis}|${selectedCoachId}` && reviewMembers.ready && reviewMembers.ask > 0 && <span className="text-[13px]">
                            라포에 알릴 회원 {reviewMembers.ask}건{reviewMembers.submitted !== undefined && ` · 라포 확인 중 ${reviewMembers.submitted}건`}
                        </span>}
                        <span className="ml-auto flex shrink-0 items-center gap-[5px] pl-[16px] font-[650]">보기 <ReportIcon name="right" /></span>
                    </button> : <div className="flex min-h-[52px] items-center gap-[8px] rounded-[12px] border border-solid border-[#D8EDE2] bg-[#EFF8F3] px-[17px] text-[14px] text-[#35755C]">
                        <ReportIcon name="check" />{resolvedCount > 0 ? `손볼 회원은 없어요 · 처리 완료 ${resolvedCount}건` : `${isCurrentMonth ? '이번 달은' : `${month}월은`} 손볼 회원이 없어요`}
                        <button type="button" onClick={() => setShowReview(true)} className="ml-auto text-[#35755C]">안내 보기</button>
                    </div>}
                {unlistedSummary && <div role="note" aria-label="회원 목록 밖 확인 기록" className="mt-[8px] flex items-center gap-[10px] text-[14px] text-[#4E5968]">
                    <ReportIcon name="alert" />
                    <p>회원 목록 밖에 확인할 기록이 있어요 · {unlistedSummary}</p>
                    <button type="button" onClick={() => setShowReview(true)} className="ml-auto shrink-0 font-semibold">안내 보기 ›</button>
                </div>}
            </section>
            {evidence?.basis === evidenceBasis && <SessionReportEvidenceDrawer key={`${evidenceBasis}|${evidence.selection.metric}`} centerId={centerId} report={report}
                selectedReport={coachState.isError ? undefined : coachState.report} selectedCoachId={selectedCoachId || (!report.centerTotals ? report.coaches[0]?.coachId : '')}
                selection={evidence.selection} paused={!!exportProgress || isRecalculating} onSyncSummary={onSyncSummary} onClose={() => setEvidence(null)}
                renderDetail={(row, onClose) => <ReviewDetailLayer><ReviewMemberDetail key={`${row.coachId}|${row.rowKey}`} row={row} centerId={centerId} report={report} onClose={onClose} /></ReviewDetailLayer>} />}
            <ReviewDrawer open={showReview} detailOpen={reviewDetailOpen} onClose={() => setShowReview(false)}>
                    <SessionReportReviewList
                        key={reviewBasis}
                        centerId={centerId} report={report} selectedReport={coachState.isError ? undefined : coachState.report}
                        selectedCoachId={selectedCoachId}
                        resolvedRequest={resolvedRequest}
                        summaryNotice={reviewSummary}
                        open={showReview} onToggle={() => setShowReview((value) => !value)} onRecalculate={onRecalculate} onSyncSummary={onSyncSummary}
                        onMemberCountsChange={() => {}}
                        onSummaryCountsChange={(counts) => setReviewMembers({ basis: `${reviewBasis}|${selectedCoachId}`, ...counts })}
                        onDetailCoachChange={handleReviewDetailChange}
                        paused={!!exportProgress || isRecalculating}
                        renderDetail={(row, onClose) => <ReviewDetailLayer><ReviewMemberDetail key={`${row.coachId}|${row.rowKey}`} row={row} centerId={centerId} report={report} onClose={onClose} /></ReviewDetailLayer>}
                    />
                <div hidden={!showReview} className="pb-[12px]">
                    <details className="text-[14px] leading-[21px] text-[#4E5968]">
                    <summary className="cursor-pointer py-[8px]">이유별 안내 보기</summary>
                    <p className="my-[12px]">아래 건수는 이유별이에요. 한 회원 기록에 이유가 여러 개 있을 수 있어요.</p>
                    {reviewLevels.filter((level) => reasonCounts(level) > 0).map((level) => {
                        const tone = REVIEW_TONES[level];
                        return <section key={level} aria-label={tone.label} className="rounded-[12px] px-[16px] py-[4px]" style={{ backgroundColor: tone.bg }}>
                            <h3 className="py-[12px] text-[14px] font-semibold leading-[22px]" style={{ color: tone.text }}>{tone.label}</h3>
                            {level === 'ask' && <p className="pb-[12px] text-[14px] leading-[22px] text-[#4E5968]">{supportGuidance}</p>}
                            <ul className="divide-y divide-[#E5E8EB]">
                                {checkFlags.filter((item) => flagReviewLevel(item.key) === level).map(({ key, count }) => {
                                    const target = coachesForReason(key)[0];
                                    const missingRecordAction = '라포에 이 달 기록 확인을 요청해 주세요';
                                    const content = <>
                                        <span className="min-w-0 flex-1 break-keep">
                                            <span className="block text-[15px] font-medium leading-[24px] text-[#333D4B]">{describeReviewIssue(key)}</span>
                                            <span className="mt-[4px] block text-[14px] leading-[21px] text-[#4E5968]">{!target && level === 'ask' ? missingRecordAction
                                                : level === 'ask' ? supportGuidance : describeReviewAction(key)}</span>
                                        </span>
                                        <span className="shrink-0 text-[14px] font-semibold" style={{ color: tone.text }}>{displayNumber(count)}건</span>
                                        {target && <span aria-hidden="true" className="text-[18px] text-[#4E5968]">›</span>}
                                    </>;
                                    return <li key={key}>
                                        {target ? <button type="button" onClick={() => selectReason(key, target.coachId)}
                                            className="flex w-full items-center gap-[12px] py-[16px] text-left hover:opacity-80">{content}</button>
                                            : <div className="flex items-center gap-[12px] py-[16px]">{content}</div>}
                                        <details className="pb-[12px] text-[14px] leading-[21px] text-[#4E5968]">
                                            <summary className="w-fit cursor-pointer py-[4px]">자세히</summary>
                                            {HANDOFF_FLAGS.has(key) && <p className="mt-[8px]">{HANDOFF_EXPLAINER}</p>}
                                            <p className="mt-[8px]">{reviewReasonText(key)}</p>
                                            <p className="mt-[4px]">{!target && level === 'ask' ? `회원 목록에 없는 기록이에요. ${missingRecordAction}.`
                                                : level === 'ask' ? supportGuidance : describeFlagAction(key)}</p>
                                            {!target && level !== 'ask' && <p className="mt-[4px]">이 이유의 회원 목록은 지금 열 수 없어요.</p>}
                                        </details>
                                    </li>;
                                })}
                            </ul>
                        </section>;
                    })}
                    {mismatchTotal > 0 && <button type="button" className="min-h-[40px] text-[14px] text-primary underline underline-offset-4"
                        onClick={() => { setShowReview(false); setReasonFilter(null); onSelectCoach(firstCoachWith('mismatch') ?? report.coaches[0].coachId, 'mismatch'); }}>강사별 확인할 회원 보기</button>}
                    {mismatchTotal === 0 && !reviewLevels.some((level) => reasonCounts(level) > 0) && <p className="text-[14px] leading-[22px] text-[#4E5968]">확인할 일은 없어요.</p>}
                    {checkFlags.some((item) => flagReviewLevel(item.key) === 'info') && <details className="text-[14px] leading-[21px] text-[#4E5968]">
                        <summary className="w-fit cursor-pointer py-[8px]">할 일 없는 안내 보기</summary>
                        {checkFlags.filter((item) => flagReviewLevel(item.key) === 'info').map(({ key, label, count }) => <p key={key} className="mt-[8px]">{label} {count}건 · {describeReviewIssue(key)}</p>)}
                        <button type="button" className="mt-[8px] min-h-[40px] text-primary underline underline-offset-4"
                            onClick={() => { setShowReview(false); (selectedCoachId ? memberSectionRef : coachSectionRef).current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }}>
                            {selectedCoachId ? '회원 목록 보기' : '강사 목록 보기'}
                        </button>
                    </details>}
                    </details>
                </div>
                {(countAll('expired') > 0 || countAll('ghost') > 0) && <div className="flex flex-wrap items-center gap-x-[25px] gap-y-[8px] rounded-b-[20px] border-t border-solid border-[#E7EBF0] bg-[#FCFDFF] px-[26px] py-[13px] text-[14px] leading-[22px] text-[#4E5968]">
                    <span>이 회원들도 챙겨보세요</span>
                    {countAll('expired') > 0 && <button type="button" className="min-h-[40px] hover:text-primary" onClick={() => { setShowReview(false); onSelectCoach(firstCoachWith('expired') ?? report.coaches[0].coachId, 'expired'); }}>
                        기간 지남 <strong className="ml-[8px] text-[#333D4B]">{displayNumber(countAll('expired'))}건</strong> <span aria-hidden="true">›</span>
                    </button>}
                    {countAll('ghost') > 0 && <button type="button" className="min-h-[40px] hover:text-primary" onClick={() => { setShowReview(false); onSelectCoach(firstCoachWith('ghost') ?? report.coaches[0].coachId, 'ghost'); }}>
                        {report.ghostDays}일 넘게 안 옴 <strong className="ml-[8px] text-[#333D4B]">{displayNumber(countAll('ghost'))}건</strong> <span aria-hidden="true">›</span>
                    </button>}
                </div>}
            </ReviewDrawer>

            {/* 강사별 요약(총괄) */}
            <div ref={coachSectionRef} className="scroll-mt-[90px]" />
            {selectedCoachId ? <button type="button" onClick={() => onSelectCoach('')} className="mt-[24px] self-start text-[14px] text-[#2B5DB3]">다른 강사 보기 ›</button> : <CoachSummaryTable
                key={reviewBasis}
                coachSearch={coachSearch} onCoachSearch={setCoachSearch}
                onlyReview={onlyReview} onOnlyReview={setOnlyReview}
                expanded={showTableDetail}
                onToggle={() => setShowTableDetail((v) => !v)}
                report={report}
                showInOut={showInOut}
                selectedCoachId={selectedCoachId}
                onSelectCoach={onSelectCoach}
            />}

            {/* 회원별 */}
            <div ref={memberSectionRef} className="scroll-mt-[90px]" />
            {!selectedCoach ? null : (
                <section aria-label="회원별 세션" className="mt-[42px]">
                    <div className={reportCard}>
                    <div className="flex flex-wrap items-center gap-[12px] rounded-t-[16px] border-b border-solid border-[#E3EAF7] bg-[#F4F7FE] px-[22px] py-[19px]">
                        <CoachAvatar name={selectedCoach.coachName} />
                        <div><h2 className="text-[18px] font-bold">{selectedCoach.coachName} 강사 · 회원별 세션</h2><p className="mt-px text-[13px] text-[#4E5968]">{report.yearMonth.replace('-', '년 ')}월</p></div>
                        <div className="ml-auto flex flex-wrap items-center gap-[12px]">
                            {memberSearchData.length > 0 && <label className="flex h-[40px] w-[178px] items-center gap-[7px] rounded-[8px] border border-solid border-[#E2E7EE] bg-white px-[10px]">
                                <ReportIcon name="search" className="!h-[16px] !w-[16px] text-[#4E5968]" />
                                <input aria-label="회원 검색" placeholder="회원 검색" value={search} maxLength={30} onChange={(event) => onSearch(event.target.value)} className="min-w-0 w-full bg-transparent text-[13px] placeholder:text-[#4E5968]" />
                            </label>}
                            <DetailToggle expanded={showTableDetail} onToggle={() => setShowTableDetail((v) => !v)} />
                        </div>
                    </div>
                    <div role="tablist" aria-label="회원 묶음" className="flex items-center gap-[25px] overflow-x-auto border-b border-solid border-[#E9EDF2] px-[24px]">
                        {([...SESSION_REPORT_SHEETS.map(({ key }) => key), 'mismatch'] as TabKey[]).map((key) =>
                            <SheetTab key={key} title={tabTitle(key)} count={selectedTabCount(selectedCoach, key)} isSelected={tab === key} onClick={() => { setReasonFilter(null); onTabChange(key); }} />)}
                    </div>
                    {onOpenGhostSettings && <div className="px-[24px] py-[10px] text-[13px] text-[#2B5DB3]">
                        <button type="button" onClick={(event) => {
                            // 상단 더보기의 바깥 클릭 닫기가 바로 실행되지 않게 해요.
                            event.stopPropagation();
                            onOpenGhostSettings();
                        }}>기준 바꾸기 ›</button>
                    </div>}
                    {showExplanations && <p className="px-[24px] py-[12px] text-[14px] leading-[21px] text-[#4E5968]">
                        {tab === 'valid' ? '기간 지남·안 옴에 들지 않은 수강권이에요. 만료일이 안 지났거나, 지났어도 남은 회차가 없는 수강권이에요.' : tab === 'expired' ? '기간이 지났지만 회차가 남은 수강권이에요.'
                            : tab === 'ghost' ? `기간이 지났는데 회차가 남은 수강권 중, 마지막 출석(출석이 없으면 시작일) 뒤 ${report.ghostDays}일이 지난 회원이에요. 연락해 봐 주세요.` : '기록을 고치거나 한 번 봐야 하는 회원이에요. 빨간색부터 봐 주세요.'}
                        {' 회원 이름이나 확인할 일을 누르면 수강권 정보를 볼 수 있어요.'}
                    </p>}
                    {tab !== 'valid' && countCenter(tab) > coachTabCount(selectedCoach, tab) && (
                        <div className="session-report-coach-jump flex flex-wrap items-center gap-[8px] px-[24px] py-[14px]">
                            <RegularText fontSize={14} lineHeight={19} color="#4E5968" className="mr-[2px]">
                                {`${tabTitle(tab)} 다른 강사`}
                            </RegularText>
                            <CoachJumpRow
                                coaches={report.coaches}
                                tab={tab}
                                selectedCoachId={selectedCoachId}
                                onSelect={onSelectCoach}
                                onlyWithCount
                            />
                        </div>
                    )}
                    {activeReason && <div aria-label="선택한 확인 이유" className="mx-[24px] my-[16px] rounded-[12px] bg-[#F4F7FF] p-[16px] text-[14px] leading-[22px]">
                        <div className="flex flex-wrap items-center justify-between gap-[12px]">
                            <p className="font-medium text-[#333D4B]">{describeReviewIssue(activeReason)}</p>
                            <button type="button" onClick={() => setReasonFilter(null)} className="min-h-[40px] text-primary underline underline-offset-4">모든 이유 보기</button>
                        </div>
                        {coachesForReason(activeReason).length > 1 && <div className="mt-[8px] flex flex-wrap gap-[8px]">
                            {coachesForReason(activeReason).map((coach) => <button type="button" key={coach.coachId} aria-pressed={coach.coachId === selectedCoachId}
                                onClick={() => selectReason(activeReason, coach.coachId)} className={`min-h-[40px] rounded-[8px] px-[12px] text-[14px] ${coach.coachId === selectedCoachId ? 'bg-primary text-white' : 'bg-white text-[#2B529D]'}`}>{coach.coachName}</button>)}
                        </div>}
                    </div>}
                    {coachState.isLoading ? (
                        <Col className="w-full">
                            <RegularText fontSize={14} lineHeight={20} color="#4E5968" className="mb-[10px]">
                                {`${selectedCoach.coachName} 강사 회원 자료를 불러오고 있어요`}
                            </RegularText>
                            {[0, 1, 2, 3, 4].map((i) => (
                                <div key={i} className="w-full h-[52px] bg-[#F8F9FB] animate-pulse mb-[6px] rounded-[4px]" />
                            ))}
                        </Col>
                    ) : coachState.isError || !coachDetail ? (
                        <StateMessage
                            title={`${selectedCoach.coachName} 강사 회원 자료를 불러오지 못했어요`}
                            description={coachState.errorText ?? '잠시 후 다시 시도해 주세요.'}
                            actionText="다시 시도"
                            onAction={coachState.retry}
                        />
                    ) : (
                        <MemberTable
                            expanded={showTableDetail}
                            totals={tab !== 'mismatch' && !keyword && !activeReason ? coachDetail.sheets[tab].totals : undefined}
                            ghostDays={report.ghostDays}
                            rows={visibleRows}
                            onOpenDetail={(row) => setDetailRowKey(row.rowKey)}
                            showSheet={tab === 'mismatch'}
                            showInOut={showInOut}
                            emptyText={
                                keyword
                                    ? `「${keyword}」 회원이 이 목록에 없어요`
                                    : tab === 'ghost'
                                      ? `${report.ghostDays}일 넘게 수업이 없는 회원이 없어요`
                                      : tab === 'mismatch'
                                        ? '확인할 회원이 없어요'
                                        : '이 목록에 해당하는 회원이 없어요'
                            }
                        />
                    )}
                    </div>
                    <p className="mt-[12px] text-[13px] text-[#4E5968]">선택 탭 합계는 해당 탭 전체 기준이에요. 기간 안·기간 지남·안 옴을 모두 합하면 강사 전체 값이에요.</p>
                </section>
            )}
            {detailRow && (
                <MemberDetailModal
                    key={`${centerId}|${report.yearMonth}|${detailRow.coachId}|${detailRow.rowKey}`}
                    row={detailRow}
                    centerId={centerId}
                    yearMonth={report.yearMonth}
                    generatedAt={coachState.report?.generatedAt}
                    ghostDays={report.ghostDays}
                    onClose={() => setDetailRowKey(null)}
                    onOpenMembership={(m) =>
                        setOpenMembership({ membershipId: m.membershipId, coachName: detailRow.coachName, memberName: detailRow.memberName })
                    }
                />
            )}
            {detailRow && openMembership && (
                <SessionReportMembershipInfo
                    membershipId={openMembership.membershipId}
                    coachName={openMembership.coachName}
                    memberName={openMembership.memberName}
                    onClose={() => setOpenMembership(null)}
                />
            )}
        </Col>
        </ReportExplanationContext.Provider>
        </ReportMonthContext.Provider>
        </SupportContext.Provider>
    );
};

/** 같은 조건 다시 계산이 실패해 이전 결과를 보이는 중이라는 안내(11·12회차). */
const RefetchFailureBanner = ({
    failure,
    onRecalculate,
}: {
    failure: { title: string; detail: string };
    onRecalculate?: () => void;
}) => (
    <Col className="mb-[16px] px-[16px] py-[12px] border border-solid border-WARNING bg-WARNING_50 rounded-[10px] gap-[2px]">
        <Row className="items-center gap-[8px]">
            <SvgIcon name="SvgWarningInCircle" size={17} fill={colors.WARNING} />
            <MediumText fontSize={14} lineHeight={20} color={colors.WARNING}>
                {failure.title}
            </MediumText>
            {onRecalculate && (
                <span
                    role="button"
                    tabIndex={0}
                    className="cursor-pointer underline underline-offset-2"
                    onClick={onRecalculate}
                    onKeyDown={(e) => e.key === 'Enter' && onRecalculate()}
                >
                    <RegularText fontSize={14} lineHeight={20} color={colors.WARNING}>
                        다시 계산
                    </RegularText>
                </span>
            )}
        </Row>
        <RegularText fontSize={14} lineHeight={19} color={colors.WARNING} className="pl-[25px] break-keep">
            {failure.detail}
        </RegularText>
    </Col>
);

/** 강사 바로가기 알약 — 탭별 줄 수(서버 값)와 함께. */
const CoachJumpRow = ({
    coaches,
    tab,
    selectedCoachId,
    onSelect,
    onlyWithCount = false,
}: {
    coaches: SessionReportCoach[];
    tab: TabKey;
    selectedCoachId: string;
    onSelect: (coachId: string, tab?: TabKey) => void;
    onlyWithCount?: boolean;
}) => (
    <div className="flex min-w-0 flex-wrap items-center gap-[8px]">
        {coaches
            .filter((c) => !onlyWithCount || coachTabCount(c, tab) > 0)
            .map((c) => {
                const isSelected = c.coachId === selectedCoachId;
                const isWarning = tab === 'mismatch';
                return (
                    <button type="button" aria-pressed={isSelected}
                        key={c.coachId}
                        className="inline-flex max-w-full items-center min-h-[34px] px-[14px] py-[5px] rounded-[17px] cursor-pointer border border-solid"
                        style={{
                            backgroundColor: isSelected ? (isWarning ? WARNING_BG : '#F4F7FF') : 'white',
                            borderColor: isSelected ? (isWarning ? WARNING_BORDER : '#D9E6FF') : '#E4E4E4',
                        }}
                        onClick={() => onSelect(c.coachId, tab)}
                    >
                        <RegularText fontSize={14} lineHeight={20} color="#3D4244">
                            {c.coachName}
                        </RegularText>
                        <RegularText
                            fontSize={14}
                            lineHeight={19}
                            color={isWarning ? WARNING_TEXT : '#4E5968'}
                            className="ml-[6px]"
                        >
                            {displayNumber(coachTabCount(c, tab))}
                        </RegularText>
                    </button>
                );
            })}
    </div>
);

// ───────────────────────────── 센터 합계 ─────────────────────────────

// ───────────────────────────── 강사별 요약 ─────────────────────────────

const IN_OUT_TITLES = new Set(['IN', 'OUT']);
const SUMMARY_COLUMNS: { title: string; width: number }[] = [
    { title: '강사', width: 126 },
    { title: '신규', width: 112 },
    { title: '변경', width: 104 },
    { title: '리뉴', width: 112 },
    { title: '인계', width: 120 },
    { title: '계약미수금', width: 104 },
    { title: '전월잔여', width: 116 },
    { title: '당월등록', width: 116 },
    { title: '당월완료', width: 116 },
    { title: '남은세션', width: 116 },
    { title: 'IN', width: 132 },
    { title: 'OUT', width: 132 },
    { title: '환불', width: 112 },
    { title: '확인', width: 190 },
];

const AmountCell = ({ amount, sessions, bold = false }: { amount: number; sessions: number; bold?: boolean }) => {
    const Text = bold ? BoldText : RegularText;
    return !amount && !sessions ? (
        <Text fontSize={14} lineHeight={20} color="#4E5968">
            -
        </Text>
    ) : (
        <Col className="items-end">
            <Text fontSize={14} lineHeight={20} color="#3D4244">
                {amount === 0 ? <ReportTableNumber value={amount} /> : won(amount)}
            </Text>
            <Text fontSize={14} lineHeight={19} color="#4E5968">
                {sessions === 0 ? <ReportTableNumber value={sessions} /> : times(sessions)}
            </Text>
        </Col>
    );
};

const NumberCell = ({ value, bold }: { value: number; bold?: boolean }) => {
    const Text = bold ? BoldText : RegularText;
    return (
        <Text fontSize={14} lineHeight={20} color={value ? '#3D4244' : '#4E5968'}>
            {value ? displayNumber(value) : '-'}
        </Text>
    );
};

/** 엑셀과 공유하는 원문은 유지하고, 끝의 회차 숫자만 화면용 부호로 바꾼다. 강사 이름은 건드리지 않는다. */
const screenHandoffs = (handoffs: SessionReportCoach['summary']['handoffs'] | undefined, separator = ', ') =>
    (handoffs ?? []).map((handoff) => {
        const entry = formatHandoffs([handoff]);
        return handoff.sessions < 0
            ? entry.slice(0, -String(handoff.sessions).length) + formatReportNumber(handoff.sessions)
            : entry;
    }).join(separator);

const CoachSummaryTable = ({
    report,
    showInOut,
    selectedCoachId,
    onSelectCoach,
    expanded,
    onToggle,
    coachSearch,
    onCoachSearch,
    onlyReview,
    onOnlyReview,
}: {
    coachSearch: string;
    onCoachSearch: (value: string) => void;
    onlyReview: boolean;
    onOnlyReview: (value: boolean) => void;
    expanded: boolean;
    onToggle: () => void;
    report: SessionReport;
    /** false 면 IN·OUT 열과 「근무시간 미설정」 표시를 숨긴다(hasInOutSplit) */
    showInOut: boolean;
    selectedCoachId: string;
    onSelectCoach: (coachId: string, tab?: TabKey) => void;
}) => {
    const t = report.centerTotals;
    const displayedCenterReviewCount = screenCheckNeededCount(t?.reference);
    const centerReviewStatus = displayedCenterReviewCount > 0 ? `${displayNumber(displayedCenterReviewCount)}건 확인 필요` : hasReportReviewWork(t?.reference) ? '별도 확인 기록' : '손볼 회원 없음';
    const reviewCount = (coach: SessionReportCoach) => screenCheckNeededCount(coach.summary.reference);
    const hasSummaryOnlyReview = (coach: SessionReportCoach) => reviewCount(coach) === 0
        && hasReportReviewWork(coach.summary.reference);
    const summaryOnlyStatus = <span className="text-[13px] text-[#9A4B08]">별도 확인 기록</span>;
    const selectRow = (coach: SessionReportCoach) => onSelectCoach(coach.coachId);
    // 기본 보기에서만 이 달 숫자와 확인할 기록이 없는 강사를 접어 둔다.
    const [showQuiet, setShowQuiet] = useState(false);
    const isQuiet = (c: SessionReportCoach) => {
        const s = c.summary;
        return c.coachId !== selectedCoachId && !s.prevRemaining && !s.registeredSessions && !s.progressed && !s.remaining
            && !s.handoffSessions && !s.refundSessions && !s.newAmount && !s.upgradeAmount && !s.renewAmount && !s.refundAmount
            && !s.unpaidAmount && !s.closed && reviewCount(c) === 0 && !hasSummaryOnlyReview(c);
    };
    const quietCount = report.coaches.filter(isQuiet).length;
    const shownCoaches = (expanded || showQuiet || coachSearch.trim() ? report.coaches : report.coaches.filter((c) => !isQuiet(c)))
        .filter((coach) => (!onlyReview || reviewCount(coach) > 0 || hasSummaryOnlyReview(coach)) && coach.coachName.includes(coachSearch.trim()));
    const columns = expanded
        ? SUMMARY_COLUMNS.filter((c) => showInOut || !IN_OUT_TITLES.has(c.title))
        : [{ title: '강사', width: 140 }, { title: '당월완료', width: 140 }, { title: '남은세션', width: 150 }, { title: '이번 달 결제', width: 240 }, { title: '확인', width: 160 }];
    const renderFullCells = (s: SessionReportSummaryBase, handoffText: string, bold = false) => [
        <AmountCell key="new" amount={s.newAmount} sessions={s.newSessions} bold={bold} />,
        <AmountCell key="up" amount={s.upgradeAmount} sessions={s.upgradeSessions} bold={bold} />,
        <AmountCell key="renew" amount={s.renewAmount} sessions={s.renewSessions} bold={bold} />,
        <Col key="handoff" className="items-end">
            {handoffText ? (
                <RegularText fontSize={14} lineHeight={18} color="#4E5968" className="whitespace-pre-line">
                    {handoffText}
                </RegularText>
            ) : null}
            <MediumText fontSize={14} lineHeight={20} color={s.handoffSessions ? '#3D4244' : '#4E5968'} className={bold ? 'font-noto-bold' : 'font-noto-regular'}>
                {s.handoffSessions ? times(s.handoffSessions) : '-'}
            </MediumText>
        </Col>,
        <Col key="unpaid" className="items-end">
            <MediumText fontSize={14} lineHeight={20} color={s.unpaidAmount ? '#3D4244' : '#4E5968'} className={bold ? 'font-noto-bold' : 'font-noto-regular'}>
                {dashIfZero(s.unpaidAmount, won)}
            </MediumText>
            {(s.reference?.unpaidUnknownRowCount ?? 0) > 0 && (
                <RegularText fontSize={14} lineHeight={18} color={WARNING_TEXT}>
                    {`확인 필요 ${s.reference.unpaidUnknownRowCount}줄`}
                </RegularText>
            )}
        </Col>,
        <NumberCell key="prev" value={s.prevRemaining} bold={bold} />,
        <NumberCell key="reg" value={s.registeredSessions} bold={bold} />,
        <NumberCell key="prog" value={-s.progressed} bold={bold} />,
        <NumberCell key="rem" value={s.remaining} bold />,
        ...(showInOut
            ? [<NumberCell key="in" value={s.inCount} bold={bold} />, <NumberCell key="out" value={s.outCount} bold={bold} />]
            : []),
        <AmountCell key="refund" amount={s.refundAmount} sessions={s.refundSessions} bold={bold} />,
    ];

    const renderCells = renderFullCells;
    const compactCells = (s: SessionReportSummaryBase) => <>
        <td><ReportTableNumber value={s.prevRemaining} /></td>
        <td><ReportTableNumber value={s.registeredSessions} />{(s.newSessions !== 0 || s.renewSessions !== 0 || s.upgradeSessions !== 0) && <small>{(s.newSessions !== 0 || s.renewSessions !== 0) && <>신규 {displayNumber(s.newSessions)} · 재등록(리뉴) {displayNumber(s.renewSessions)}</>}{s.upgradeSessions !== 0 && `${s.newSessions !== 0 || s.renewSessions !== 0 ? ' · ' : ''}추가(세션업) ${displayNumber(s.upgradeSessions)}`}</small>}</td>
        <td><ReportTableNumber value={-s.progressed} signed />{showInOut && (s.inCount !== 0 || s.outCount !== 0) && <small>IN {displayNumber(s.inCount)} · OUT {displayNumber(s.outCount)}</small>}</td>
        <td><ReportTableNumber value={s.handoffSessions} signed /></td>
        <td><ReportTableNumber value={s.refundSessions} /></td>
        <td><ReportTableNumber value={s.closed ?? 0} /></td>
        <td className="font-[720] text-[#273343]"><ReportTableNumber value={s.remaining} /></td>
        <td><ReportTableNumber value={s.newAmount + s.upgradeAmount + s.renewAmount} /></td>
    </>;
    return (
        <section aria-label="강사별 세션" className="mt-[31px] w-full">
            <div className="mb-[14px] flex flex-wrap items-center justify-between gap-[16px]">
                <h2 className="text-[20px] font-bold leading-[1.4] tracking-[-.04em]">강사별 세션 <span className="ml-[9px] text-[15px] font-medium text-[#4E5968]">{report.coaches.length}명</span></h2>
                <div className="flex flex-wrap items-center gap-[20px]">
                    <label className="flex h-[40px] w-[178px] items-center gap-[7px] rounded-[8px] border border-solid border-[#E2E7EE] bg-white px-[10px] focus-within:border-[#3D73DD] focus-within:shadow-[0_0_0_2px_#3D73DD15]">
                        <ReportIcon name="search" className="!h-[16px] !w-[16px] text-[#4E5968]" />
                        <input aria-label="강사 이름 검색" placeholder="강사 이름 검색" value={coachSearch} onChange={(event) => onCoachSearch(event.target.value)} className="min-w-0 w-full bg-transparent text-[13px] placeholder:text-[#4E5968]" />
                    </label>
                    <label className="relative flex cursor-pointer items-center gap-[8px] whitespace-nowrap text-[13px] text-[#4E5968]">
                        <input type="checkbox" role="switch" aria-label="손볼 일 있는 강사만" checked={onlyReview} onChange={(event) => onOnlyReview(event.target.checked)} className="peer sr-only" />
                        <span className="inline-flex h-[18px] w-[30px] rounded-[12px] bg-[#BAC3CE] p-[3px] after:h-[12px] after:w-[12px] after:rounded-full after:bg-white after:shadow-[0_1px_2px_#0002] after:transition-transform peer-checked:bg-[#3D73DD] peer-checked:after:translate-x-[12px] peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-[#3D73DD]" />손볼 일 있는 강사만
                    </label>
                    <DetailToggle expanded={expanded} onToggle={onToggle} />
                </div>
            </div>
            {!expanded ? <div className={`${reportCard} overflow-hidden`}>
                <div className="max-h-[min(1400px,calc(100vh-180px))] overflow-auto">
                <table className={`${reportTable} [&_tfoot_td:nth-child(8)]:text-[18px] [&_tfoot_td:nth-child(8)]:text-[#3D73DD]`} aria-label="강사별 세션 표">
                    <colgroup>{[150, 100, 170, 70, 110, 65, 85, 110, 170, 170].map((width, index) => <col key={index} style={{ width: `${width / 12}%` }} />)}</colgroup>
                    <thead><tr>{[['강사', '총괄 시트'], ['지난달 남은', '전월잔여'], ['새로 판', '당월등록'], ['수업', '당월완료'], ['강사 간 넘김', '인계'], ['환불', '환불'], ['취소·정리', ''], ['남은 회차', '남은세션'], ['결제', '금액 · 원'], ['상태', '']].map(([title]) => <th key={title} scope="col" className={title === '지난달 남은' ? 'text-right' : ''}>{title}</th>)}</tr></thead>
                    <tbody>
                        {shownCoaches.map((coach, index) => {
                            const count = reviewCount(coach);
                            const level = topReviewLevel(coach.summary.reference);
                            const tone = level === 'fix' ? { bg: '#FEF0F0', text: '#C84444' } : level === 'ask' ? { bg: '#EDF3FF', text: '#3D73DD' } : REVIEW_TONES.review;
                            return <tr key={coach.coachId} onClick={() => selectRow(coach)} className={`cursor-pointer ${coach.coachId === selectedCoachId ? '[&_td]:!bg-[#F2F6FE] [&_td:first-child]:shadow-[inset_3px_0_#3D73DD]' : ''}`}>
                                <td><button type="button" aria-label={`${coach.coachName} 강사 회원 보기`} aria-pressed={coach.coachId === selectedCoachId} onClick={(event) => { event.stopPropagation(); selectRow(coach); }} className="flex w-full items-center gap-[10px] text-left text-[15px] font-semibold"><CoachAvatar name={coach.coachName} index={index} /><span className="whitespace-normal">{coach.coachName}{coach.isDeleted && !/\(퇴사\)/.test(coach.coachName) && <small>퇴사</small>}</span>{coach.coachId === selectedCoachId && <ReportIcon name="right" className="ml-auto !w-[14px] text-[#3D73DD]" />}</button></td>
                                {compactCells(coach.summary)}
                                <td>{count ? <button type="button" title={REVIEW_BADGE_HINT[level ?? 'review']} onClick={(event) => { event.stopPropagation(); onSelectCoach(coach.coachId, 'mismatch'); }} className={reportPill} style={{ backgroundColor: tone.bg, color: tone.text }}>{`${count}건 확인 필요`}</button> : hasSummaryOnlyReview(coach) ? summaryOnlyStatus : <GoodStatus />}</td>
                            </tr>;
                        })}
                        {shownCoaches.length === 0 && <tr><td colSpan={10} className="!h-[140px] !text-center !text-[14px] text-[#4E5968]">조건에 맞는 강사가 없어요.</td></tr>}
                        {quietCount > 0 && !onlyReview && !coachSearch.trim() && <tr><td colSpan={10} className="!h-[43px] !bg-[#FCFDFE] !p-0 !text-center"><button type="button" onClick={() => setShowQuiet((value) => !value)} className="flex h-[43px] w-full items-center justify-center gap-[8px] text-[13px] text-[#4E5968] hover:bg-[#F1F5FB]">{showQuiet ? '숫자가 없는 강사 접기' : `이 달 숫자가 모두 0인 강사 ${quietCount}명 더 보기`}<ReportIcon name="down" className="!w-[14px]" /></button></td></tr>}
                    </tbody>
                    {t && <tfoot aria-label="센터 합계"><tr><td><span className="text-[15px]">센터 합계</span><small>전체 {report.coaches.length}명</small></td>{compactCells(t)}<td><button type="button" className="text-[13px] font-medium text-[#4E5968]" onClick={() => { const first = report.coaches.find((coach) => reviewCount(coach) > 0); if (first) onSelectCoach(first.coachId, 'mismatch'); }}>{centerReviewStatus}</button></td></tr></tfoot>}
                </table>
                </div>
            </div> : <>
            <div className="w-full max-h-[min(1400px,calc(100vh-180px))] overflow-auto rounded-[10px] scrollbar scrollbar-h-[10px] scrollbar-thumb-[#4E5968] scrollbar-track-[#EEF0F2]">
            <table aria-label="강사별 엑셀 모든 칸" style={{ minWidth: columns.reduce((sum, c) => sum + c.width, 0) }} className="w-full border-collapse text-center table-fixed [&_th]:sticky [&_th]:top-0 [&_th]:z-[3]">
                <colgroup>
                    {columns.map((c) => (
                        <col key={c.title} style={{ width: c.width }} />
                    ))}
                </colgroup>
                <thead>
                    <tr className="h-[40px]">
                        {columns.map((c) => (
                            <th key={c.title} className={`border border-solid border-[#D9D9D9] bg-[#F0F2F3] px-[4px] font-normal align-middle ${c.title === '강사' || c.title === '확인' ? '' : 'text-right'}`}>
                                <ReportColumnTitle column={c.title} showExcelName={expanded} />
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {shownCoaches.map((coach) => {
                        const isSelected = coach.coachId === selectedCoachId;
                        const mismatch = reviewCount(coach);
                        return (
                            <tr
                                key={coach.coachId}
                                className={`h-[58px] cursor-pointer ${isSelected ? 'bg-[#F4F7FF]' : 'hover:bg-[#F8F9FB]'}`}
                                onClick={() => selectRow(coach)}
                                tabIndex={0} onKeyDown={(event) => { if (event.target === event.currentTarget && ['Enter', ' '].includes(event.key)) { event.preventDefault(); selectRow(coach); } }}
                            >
                                <td className="border border-solid border-[#E4E4E4] px-[8px] align-middle">
                                    <Col className="items-center">
                                        <MediumText
                                            fontSize={15}
                                            lineHeight={21}
                                            color={isSelected ? colors.primary : '#3D4244'}
                                            className="underline underline-offset-4 decoration-[#C6C8C9]"
                                        >
                                            {coach.coachName}
                                        </MediumText>
                                        {coach.isDeleted && !/\(퇴사\)/.test(coach.coachName) && <span className="text-[13px] text-[#4E5968]">퇴사</span>}
                                        {expanded && showInOut && coach.workRoutineMissing && (
                                            <TooltipWrapper
                                                tooltip={{
                                                    title: '근무시간이 설정되지 않아 수업을 전부 IN 수업으로 셌어요.',
                                                    width: 230,
                                                }}
                                            >
                                                <RegularText fontSize={14} lineHeight={18} color="#4E5968">
                                                    근무시간 미설정
                                                </RegularText>
                                            </TooltipWrapper>
                                        )}
                                    </Col>
                                </td>
                                {renderCells(coach.summary, screenHandoffs(coach.summary.handoffs, ' / ')).map(
                                    (cell, i) => (
                                        <td key={i} className="border border-solid border-[#E4E4E4] px-[4px] py-[8px] align-middle text-right tabular-nums">
                                            {cell}
                                        </td>
                                    ),
                                )}
                                <td className="border border-solid border-[#E4E4E4] align-middle">
                                    {mismatch > 0 ? (
                                        <button type="button"
                                            className="min-h-[40px] cursor-pointer"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onSelectCoach(coach.coachId, 'mismatch');
                                            }}
                                        >
                                            <ReviewCountBadge count={mismatch} />
                                        </button>
                                    ) : hasSummaryOnlyReview(coach) ? summaryOnlyStatus : (
                                        <RegularText fontSize={14} lineHeight={19} color="#4E5968">
                                            맞아요
                                        </RegularText>
                                    )}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
                <tfoot className="sticky bottom-0 z-[4]">
                    {t && (
                        <tr className="h-[58px] border-t-2 border-solid border-[#B4BCC4] bg-[#F0F2F3] font-noto-bold">
                            <td className="border border-solid border-[#D9D9D9] align-middle">
                                <BoldText fontSize={15} lineHeight={21} color="#3D4244">
                                    센터 합계
                                </BoldText>
                            </td>
                            {renderCells(t, '', true).map((cell, i) => (
                                <td key={i} className="border border-solid border-[#D9D9D9] px-[4px] py-[8px] align-middle text-right tabular-nums">
                                    {cell}
                                </td>
                            ))}
                            <td className="border border-solid border-[#D9D9D9] align-middle">
                                <button type="button" className="min-h-[40px]" onClick={() => {
                                    const first = report.coaches.find((coach) => reviewCount(coach) > 0);
                                    if (first) onSelectCoach(first.coachId, 'mismatch');
                                }}><span className="text-[14px] font-semibold text-[#4E5968]">{centerReviewStatus}</span></button>
                            </td>
                        </tr>
                    )}
                </tfoot>
            </table>
            </div>
            </>}
            <div className="mt-[12px] flex flex-wrap justify-between gap-[20px] px-[2px] text-[13px] text-[#4E5968]">
                <span className="flex items-center gap-[5px]"><ReportIcon name="info" className="!h-[14px] !w-[14px]" />강사를 선택하면 아래에서 회원별 세션을 볼 수 있어요.</span>
                <span>{t && (t.closed || summaryFlowDifference(t)) ? `${[t.closed ? `취소·정리 ${displayNumber(t.closed)}회` : '', summaryFlowDifference(t) ? `기록과 안 맞는 회차 ${displayNumber(summaryFlowDifference(t))}회` : ''].filter(Boolean).join(', ')} 포함 · ` : ''}센터 합계는 강사 {report.coaches.length}명 전체 기준</span>
                {expanded && <span>표를 좌우로 밀면 나머지 칸을 볼 수 있어요 ↔</span>}
            </div>
            {/* 간단 보기에서는 강사마다 「근무시간 미설정」을 달지 않는다 — 대신 몇 명인지 한 줄로 알린다 */}
            {!expanded && showInOut && report.coaches.some((c) => c.workRoutineMissing) && (
                <p className="mt-[10px] text-[14px] leading-[22px] text-[#4E5968] break-keep">
                    {`근무시간을 정하지 않은 강사 ${report.coaches.filter((c) => c.workRoutineMissing).length}명은 수업을 모두 IN 으로 셌어요. 누구인지는 자세히 보기에서 볼 수 있어요.`}
                </p>
            )}
            <ColumnGlossary columns={columns.map((c) => c.title)} />
        </section>
    );
};

// ───────────────────────────── 회원별 표 ─────────────────────────────

// 서버의 valid 는 「만료일이 안 지남」일 뿐이라 다 썼거나 취소된 수강권도 들어 있다 — 「쓰는 중」이라 부르지 않는다.
const SHEET_SHORT: Record<SessionReportSheetKey, string> = { valid: '기간 안', expired: '기간 지남', ghost: '일 넘게 안 옴' };

const MemberTable = ({
    totals,
    expanded,
    ghostDays,
    rows,
    showSheet,
    showInOut,
    emptyText,
    onOpenDetail,
}: {
    totals?: SessionReportSheetTotals;
    expanded: boolean;
    ghostDays: number;
    rows: DisplayRow[];
    showSheet: boolean;
    /** false 면 IN·OUT 열을 숨긴다(hasInOutSplit) */
    showInOut: boolean;
    emptyText: string;
    onOpenDetail: (row: DisplayRow) => void;
}) => {
    const [page, setPage] = useState(0);
    useEffect(() => { setPage(0); }, [rows]);
    const pageCount = Math.max(1, Math.ceil(rows.length / 20));
    const pageIndex = Math.min(page, pageCount - 1);
    if (!expanded) return <div className="relative"><div className="max-h-[421px] overflow-auto rounded-b-[16px]">
        <table aria-label="회원별 세션 표" className={`${reportTable} [&_th]:whitespace-nowrap [&_th]:!h-[54px] [&_tfoot_td]:!h-[52px]`}>
            <colgroup>{[140, 220, 110, 120, 110, 125, 95, 145, 135].map((width, index) => <col key={index} style={{ width: `${width / 12}%` }} />)}</colgroup>
            <thead><tr>{['회원', '수강권', '지난달 남은', '새로 판', '수업', '강사 간 넘김', '환불', '남은 회차', '상태'].map((title) => <th key={title} scope="col" className={title === '수강권' ? 'text-left' : ''}>{title}</th>)}</tr></thead>
            <tbody>{rows.slice(pageIndex * 20, pageIndex * 20 + 20).map((row) => <tr key={row.rowKey} className="cursor-pointer" onClick={() => onOpenDetail(row)}>
                <td><button type="button" onClick={(event) => { event.stopPropagation(); onOpenDetail(row); }} className="max-w-full whitespace-normal break-words text-left text-[15px] font-medium">{row.memberName}</button>{showSheet && <small>{row.sheetKey === 'ghost' ? `${ghostDays}일 넘게 안 옴` : SHEET_SHORT[row.sheetKey]}</small>}</td>
                <td className="!text-left !text-[15px] text-[#4E5968]"><span className="block truncate" title={row.kindLabel}>{row.kindLabel || '-'}</span>{isUnlimitedRow(row) && <small>무제한</small>}{row.otherAdjustments.closed !== 0 && <small>취소·정리 {displayNumber(row.otherAdjustments.closed)}회</small>}</td>
                <td><ReportTableNumber value={row.prevRemaining} /></td><td><ReportTableNumber value={row.registeredSessions} /></td><td><ReportTableNumber value={-row.progressed} signed />{showInOut && (row.inCount !== 0 || row.outCount !== 0) && <small>IN {displayNumber(row.inCount)} · OUT {displayNumber(row.outCount)}</small>}</td>
                <td><ReportTableNumber value={row.handoffSessions} signed /></td><td><ReportTableNumber value={row.refundSessions} /></td><td className="font-[720] text-[#273343]"><MemberRemainingSessions row={row} isExpired={row.sheetKey === 'expired'} /></td>
                <td><RowCheckCell row={row} compact onOpen={() => onOpenDetail(row)} /></td>
            </tr>)}</tbody>
            {!rows.length && <tbody><tr><td colSpan={9} className="!h-[140px] !text-center !text-[14px] text-[#4E5968]">{emptyText}</td></tr></tbody>}
            {totals && <tfoot><tr><td className="!text-[14px]">선택 탭 합계</td><td className="!text-left !text-[14px] text-[#4E5968]">{totals.rowCount}건</td><td><ReportTableNumber value={totals.prevRemaining} /></td><td><ReportTableNumber value={totals.registeredSessions} /></td><td><ReportTableNumber value={-totals.progressed} signed /></td><td><ReportTableNumber value={totals.handoffSessions} signed /></td><td><ReportTableNumber value={totals.refundSessions} /></td><td className="!text-[18px] text-[#3D73DD]"><ReportTableNumber value={totals.remaining} /></td><td className="!text-[13px] !font-normal text-[#4E5968]">회차 기준</td></tr></tfoot>}

        </table>
    </div>{pageCount > 1 && <div className="relative h-[60px]"><PaginationBar firstPage={() => setPage(0)} previousPage={() => setPage(pageIndex - 1)} nextPage={() => setPage(pageIndex + 1)} canPreviousPage={pageIndex === 0} canNextPage={pageIndex === pageCount - 1} pageIndex={pageIndex} totalPageIndex={pageCount} /></div>}</div>;
    const num = (id: keyof SessionReportRow, header: string, size: number, format?: (v: number) => string) =>
        columnHelper.accessor((row) => row[id] as number, {
            id: String(id),
            header,
            size,
            enableSorting: false,
            cell: (info) => {
                const v = info.getValue();
                return (
                    <RegularText fontSize={15} lineHeight={24} color={v ? '#3D4244' : '#4E5968'}>
                        {v ? (format ? format(v) : displayNumber(v)) : '-'}
                    </RegularText>
                );
            },
        });

    const columns = [
        ...(expanded
            ? [
                  columnHelper.accessor('sheetKey', {
                      id: 'sheetKey',
                      header: '유효기간',
                      size: 150,
                      enableSorting: false,
                      cell: (info) => (
                          <RegularText fontSize={14} lineHeight={19} color="#4E5968">
                              {info.getValue() === 'ghost' ? `${ghostDays}일 넘게 안 옴` : SHEET_SHORT[info.getValue()]}
                          </RegularText>
                      ),
                  }),
              ]
            : []),
        columnHelper.accessor('memberName', {
            id: 'memberName',
            header: '회원명',
            size: 108,
            enableSorting: false,
            cell: (info) => (
                <span>
                    <span className="block w-[150px] truncate text-[15px] leading-[24px] text-[#3D4244] underline underline-offset-4 decoration-[#C6C8C9]" title={info.getValue()}>{info.getValue()}</span>
                    {showSheet && !expanded && <span className="block text-[14px] leading-[20px] text-[#4E5968]">
                        {info.row.original.sheetKey === 'ghost' ? `${ghostDays}일 넘게 안 옴` : SHEET_SHORT[info.row.original.sheetKey]}
                    </span>}
                </span>
            ),
        }),
        columnHelper.accessor('prevRemaining', {
            id: 'prevRemaining',
            header: '전월잔여',
            size: 70,
            enableSorting: false,
            cell: (info) => (
                <RegularText fontSize={15} lineHeight={24} color="#3D4244">
                    <ReportTableNumber value={info.getValue()} />
                </RegularText>
            ),
        }),
        columnHelper.accessor('kindLabel', {
            id: 'kindLabel',
            header: '구분',
            size: 156,
            enableSorting: false,
            cell: (info) => (
                <Row className="items-center justify-center max-w-[150px]">
                    {isUnlimitedRow(info.row.original) && (
                        <span className="inline-flex flex-shrink-0 items-center px-[6px] mr-[4px] rounded-[4px] text-[14px] font-semibold bg-[#EFF6FF] text-[#1E40AF]">
                            무제한
                        </span>
                    )}
                    <span title={info.getValue()} className="truncate text-[14px] leading-[19px] text-[#4E5968]">
                        {info.getValue() || '-'}
                    </span>
                </Row>
            ),
        }),
        num('registeredAmount', '등록금액', 100),
        num('registeredSessions', '세션', 52),
        ...(showInOut ? [num('inCount', 'IN', 46), num('outCount', 'OUT', 46)] : []),
        columnHelper.accessor('progressed', {
            id: 'progressed',
            header: '진행세션',
            size: 66,
            enableSorting: false,
            cell: (info) => (
                <RegularText fontSize={15} lineHeight={24} color="#3D4244">
                    <ReportTableNumber value={info.getValue()} />
                </RegularText>
            ),
        }),
        columnHelper.accessor('remaining', {
            id: 'remaining',
            header: '남은세션',
            size: 66,
            enableSorting: false,
            cell: (info) => (
                <MediumText fontSize={15} lineHeight={24} color={info.getValue() < 0 ? WARNING_TEXT : '#3D4244'}>
                    <MemberRemainingSessions row={info.row.original} isExpired={info.row.original.sheetKey === 'expired'} />
                </MediumText>
            ),
        }),
        columnHelper.accessor((row) => screenHandoffs(row.handoffs), {
            id: 'handoffs',
            header: '담당',
            size: 112,
            enableSorting: false,
            cell: (info) => (
                <span title={info.getValue()} className="truncate max-w-[108px] text-[14px] leading-[19px] text-[#4E5968]">
                    {info.getValue() || '-'}
                </span>
            ),
        }),
        num('handoffSessions', '인계세션', 64),
        num('refundAmount', '환불금액', 92),
        num('refundSessions', '환불세션', 64),
        columnHelper.accessor('unpaidAmount', {
            id: 'unpaidAmount',
            header: '미수(현재)',
            size: 92,
            enableSorting: false,
            cell: (info) =>
                isUnpaidUnknown(info.row.original) ? (
                    // null = 서버가 계산하지 않은 미수. 0 으로 보이지 않는다.
                    <TooltipWrapper tooltip={{ title: describeFlag('UNPAID_UNKNOWN'), width: 240 }}>
                        <WarningBadge text={UNPAID_UNKNOWN_TEXT} />
                    </TooltipWrapper>
                ) : (
                    <RegularText fontSize={15} lineHeight={24} color={info.getValue() ? '#3D4244' : '#4E5968'}>
                        {info.getValue() ? displayNumber(info.getValue() as number) : '-'}
                    </RegularText>
                ),
        }),
        columnHelper.display({
            id: 'check',
            header: '확인',
            size: 84,
            cell: (info) => <RowCheckCell row={info.row.original} compact={!expanded} onOpen={() => onOpenDetail(info.row.original)} />,
        }),
    ];

    const simpleColumns = new Set(['memberName', 'kindLabel', 'progressed', 'remaining', 'check']);
    // 자세히 보기는 가로로 밀어야 끝이 보인다 — 가장 먼저 볼 「확인할 일」은 회원 이름 바로 뒤로 당긴다.
    const ordered = expanded
        ? columns.flatMap((column) => (column.id === 'check' ? [] : column.id === 'memberName' ? [column, columns.find((c) => c.id === 'check')!] : [column]))
        : columns;
    const visibleColumns = ordered.filter((column) => expanded || simpleColumns.has(column.id!));
    const tableColumns = visibleColumns.map((column) => ({
        ...column,
        size: column.id === 'check' ? 300 : column.id === 'memberName' ? 170 : column.id === 'kindLabel' ? 210 : Math.max(column.size ?? 100, 120),
        header: () => !expanded && column.id === 'check' ? <span className="text-[15px] text-[#4E5968]">상태</span> : <ReportColumnTitle column={column.header as string} showExcelName={expanded} />,
    }));
    return (
        <>
        {/* 자세히 보기는 칸이 16개라 좁은 화면에서 글자가 세로로 쪼개진다 — 가로로 밀어 보게 한다 */}
        {expanded && <p className="mb-[8px] text-right text-[14px] leading-[20px] text-[#4E5968]">표를 좌우로 밀면 나머지 칸을 볼 수 있어요 ↔</p>}
        <div className="w-full overflow-x-auto scrollbar scrollbar-h-[10px] scrollbar-thumb-[#4E5968] scrollbar-track-[#EEF0F2]">
        <div style={expanded ? { minWidth: tableColumns.reduce((width, column) => width + column.size, 0) } : undefined}>
        <Table
            data={rows}
            columns={tableColumns}
            width="100%"
            isPagination
            pageSize={20}
            rowHeight={64}
            className="mb-[20px] pb-[56px]"
            emptyText={emptyText}
            getRowStyle={(row: DisplayRow) => expanded ? reviewRowStyle(row) : {}}
            onRowClick={(row: DisplayRow) => onOpenDetail(row)}
            customPaginationComponent={(p) => <PaginationBar {...p} />}
        />
        </div>
        </div>
        <ColumnGlossary columns={visibleColumns.map((column) => column.header as string)} />
        </>
    );
};

/** 행 바탕색은 직접 고치기·라포에 문의만 — 할 일 없는 행까지 칠하면 급한 행이 묻힌다. */
const reviewRowStyle = (row: DisplayRow) => {
    if (!isCheckNeededRow(row)) return {};
    const top = rowReviewReasons(row)[0];
    const level = top ? flagReviewLevel(top) : 'review';
    return level === 'fix' ? { backgroundColor: '#FEF6F6' } : level === 'ask' ? { backgroundColor: '#F5F7FF' } : {};
};

const reviewReasonLabel = (flag: string) => flag === '__mismatch' ? '계산이 안 맞음'
    : flag === 'SESSIONLESS' ? '무제한 수강권' : flag === 'SERVICE_SESSION' ? '서비스 회차' : describeFlagShort(flag);
const reviewReasonText = (flag: string) => flag === '__mismatch' ? '수업 기록으로 센 값과 수강권 기록이 달라요.' : describeFlag(flag);

const rowReviewIssue = (row: SessionReportRow, flag: string) => flag === 'COMPANION_REPRESENTATIVE_CHANGED' && isRepMovedRow(row)
    ? describeRowFlag(row, flag) : describeReviewIssue(flag);

const rowReviewAction = (row: SessionReportRow, flag: string, supportGuidance: string) => flagReviewLevel(flag) === 'ask'
    ? rowReviewReasons(row).some((reason) => flagReviewLevel(reason) === 'fix')
        ? '직접 고칠 것을 먼저 고쳐 주세요' : supportGuidance
    : screenReportText(describeReviewAction(flag));

const rowHasReviewReason = (row: SessionReportRow, key: string) => rowReviewReasons(row).some((flag) =>
    key === UNKNOWN_FLAG_KEY ? flag !== '__mismatch' && !INFO_ONLY_FLAGS.has(flag) && describeFlagShort(flag) === '기타 확인 필요' : flag === key);

const CopyMemberName = ({ name, disabled = false }: { name: string; disabled?: boolean }) => {
    const [status, setStatus] = useState('');
    useEffect(() => {
        if (!status) return;
        const timer = window.setTimeout(() => setStatus(''), 6000);
        return () => window.clearTimeout(timer);
    }, [status]);
    return <div>
        <button type="button" disabled={disabled} onClick={async () => {
            setStatus('');
            try {
                await navigator.clipboard.writeText(name);
                setStatus('회원 이름을 복사했어요. 라포 고객센터 카카오톡에 붙여 넣어 주세요');
            } catch {
                setStatus(`복사하지 못했어요. 회원 이름을 직접 복사해 주세요: ${name}`);
            }
        }} className="min-h-[42px] rounded-[10px] bg-[#3D73DD] px-[16px] text-[14px] font-medium text-white disabled:opacity-50">회원 이름 복사</button>
        {status && <p role="status" className="fixed bottom-[28px] left-1/2 z-[2100] w-max max-w-[calc(100vw-32px)] -translate-x-1/2 rounded-[12px] bg-[#333D4B] px-[20px] py-[14px] text-[14px] leading-[22px] text-white shadow-lg">{status}</p>}
    </div>;
};

const ReviewReasons = ({ row, flags = rowReviewReasons(row), centerId, loadLessonDates = false }: { row: SessionReportRow; flags?: string[]; centerId?: string; loadLessonDates?: boolean }) => {
    const supportGuidance = getSupportGuidance(useContext(SupportContext));
    return <Col className="gap-[12px]">
        {flags.map((flag) => {
            const tone = REVIEW_TONES[flagReviewLevel(flag)];
            if (BEFORE_ISSUE_FLAGS.has(flag)) {
                const memberships = row.memberships.filter((m) => m.flags.some((reason) => BEFORE_ISSUE_FLAGS.has(reason)));
                return <div key={flag} className="rounded-[10px] bg-[#F2F4F6] p-[16px] text-[14px] leading-[22px] text-[#4E5968]">
                    {memberships.length ? memberships.map((membership) => <BeforeIssueGuidance key={membership.membershipId} centerId={centerId} membership={membership} enabled={loadLessonDates} />) : <BeforeIssueGuidance />}
                </div>;
            }
            const overages = flag === 'NEGATIVE_REMAINING' ? row.memberships.filter((m) => m.remaining < 0) : [];
            const showOverages = overages.some((m) => describeMembershipSessionOverage(m));
            return <div key={flag} className="rounded-[10px] bg-[#F2F4F6] p-[16px] text-[14px] leading-[22px] text-[#4E5968]">
                {showOverages && overages.map((m) => <p key={m.membershipId} className="font-semibold" style={{ color: tone.text }}>
                    {row.memberships.length > 1 && `${m.name} · `}{describeMembershipSessionOverage(m) ?? rowReviewIssue(row, flag)}
                </p>)}
                {HANDOFF_FLAGS.has(flag) && <p className="mb-[8px]">{HANDOFF_EXPLAINER}</p>}
                <p>{flag === '__mismatch' ? `수업 기록으로 센 값과 수강권 기록이 ${formatReportNumber(row.check.diff)}회 차이 나요.` : describeRowFlag(row, flag)}</p>
                {(flag !== '__mismatch' || rowReviewReasons(row).some((reason) => flagReviewLevel(reason) === 'fix')) &&
                    <p className="mt-[4px]">{flagReviewLevel(flag) === 'ask' ? rowReviewAction(row, flag, supportGuidance) : screenReportText(describeFlagAction(flag))}</p>}
            </div>;
        })}
    </Col>;
};

const RowCheckCell = (props: { row: SessionReportRow; onOpen: () => void; compact?: boolean }) => {
    const request = useRowSupport(props.row);
    const noteId = React.useId();
    // 엑셀 칸 보기에는 문제와 단계만 남겨요. 접수 상태와 긴 안내는 회원 상세에서 확인해요.
    if (!props.compact) return <div className="w-[300px] min-w-[300px] px-[12px]"><RowReviewCell {...props} /></div>;
    const replacesAsk = props.compact && !!request && props.row.resolved !== true
        && (rowSupportPresentation(props.row, request).remaining || (isOpenSupport(request) && flagReviewLevel(rowReviewReasons(props.row)[0]) === 'ask'));
    return <div className="flex flex-col items-start">
        {!replacesAsk && <RowReviewCell {...props} />}
        {request && <button type="button" aria-label={`${props.row.memberName} · ${supportBadgeLabel(request, props.row, true)} · 상세 보기`}
            className="max-w-full text-left"
            aria-describedby={request.adminNote?.trim() ? noteId : undefined}
            onClick={(event) => { event.stopPropagation(); props.onOpen(); }}>
            <SupportBadge row={props.row} remainingProblem noteId={noteId} />
        </button>}
    </div>;
};

const RowReviewCell = ({ row, onOpen, compact = false }: { row: SessionReportRow; onOpen: () => void; compact?: boolean }) => {
    const supportGuidance = getSupportGuidance(useContext(SupportContext));
    const reasons = rowReviewReasons(row);
    if (row.resolved === true) return <button type="button" onClick={(event) => { event.stopPropagation(); onOpen(); }}
        className="py-[8px] text-[14px] text-[#15803D]">처리 완료</button>;
    // 무제한권 행은 서버가 검산하지 않는다 — 플래그가 빠진 응답이어도 「맞아요」라고 하지 않는다(기존 방어 유지).
    if (!reasons.length && isSessionlessRow(row)) return <span className="text-[14px] text-[#4E5968]">무제한 · 검산 안 함</span>;
    if (!reasons.length) return compact ? <GoodStatus /> : <span className="text-[14px] text-[#4E5968]">맞아요</span>;
    const tone = REVIEW_TONES[flagReviewLevel(reasons[0])];
    const issue = rowReviewIssue(row, reasons[0]);
    const extra = reasons.length > 1 ? ` 외 ${reasons.length - 1}` : '';
    if (!compact) return <button type="button" onClick={(event) => { event.stopPropagation(); onOpen(); }}
        aria-label={`${issue}${extra} · ${tone.label} · 상세 보기`}
        className="flex w-full min-w-0 flex-col items-start gap-[6px] py-[10px] text-left">
        <span className="flex w-full min-w-0 items-baseline gap-[4px] text-[14px] leading-[20px] font-semibold" style={{ color: tone.text }}>
            <span className="truncate" title={issue}>{issue}</span>
            {extra && <span className="shrink-0 whitespace-nowrap font-normal">{extra.trim()}</span>}
        </span>
        <span className={`${reportPill} whitespace-nowrap`} style={{ color: tone.text, backgroundColor: tone.bg }}>{tone.label}</span>
    </button>;
    return (
        <TooltipWrapper tooltip={{ customContent: <Col className="gap-[12px] text-[14px] leading-[21px]">
            {reasons.map((flag) => <div key={flag}><p className="font-medium">{rowReviewIssue(row, flag)}</p><p className="mt-[4px]">{rowReviewAction(row, flag, supportGuidance)}</p></div>)}
        </Col>, width: 340 }}>
            <button type="button" onClick={(event) => { event.stopPropagation(); onOpen(); }}
                aria-label={`${rowReviewIssue(row, reasons[0])}${reasons.length > 1 ? ` 외 ${reasons.length - 1}` : ''} ${rowReviewAction(row, reasons[0], supportGuidance)}`}
                className="inline-flex max-w-[280px] flex-col items-start gap-[6px] py-[8px] text-left"
                style={{ color: tone.text }}>
                <span className={reportPill} style={{ backgroundColor: tone.bg }}>확인 필요</span>
            </button>
        </TooltipWrapper>
    );
};

/** 통합 목록도 기존 회원 상세 창과 수강권 정보 창을 그대로 사용한다. */
const ReviewMemberDetail = ({ row, centerId, report, onClose }: {
    row: DisplayRow; centerId: string; report: SessionReport; onClose: () => void;
}) => {
    const [membershipId, setMembershipId] = useState<string | null>(null);
    return <>
        <MemberDetailModal row={row} centerId={centerId} yearMonth={report.yearMonth} ghostDays={report.ghostDays}
            generatedAt={report.generatedAt}
            onClose={onClose} onOpenMembership={(membership) => setMembershipId(membership.membershipId)} />
        {membershipId && <SessionReportMembershipInfo membershipId={membershipId} coachName={row.coachName} memberName={row.memberName} onClose={() => setMembershipId(null)} />}
    </>;
};

// ───────────────────────────── 회원 상세(수강권별) ─────────────────────────────

const MEMBERSHIP_KIND_LABEL: Record<string, string> = {
    issue: '',
    handoffCopy: '인계받음',
    transferTarget: '양도받음',
    transferTargetNoEffect: '양도 대상(재고 없음)',
};
const END_TYPE_LABEL: Record<string, string> = { handoff: '인계로 종료', canceled: '발급 취소', deleted: '삭제' };

const MemberDetailModal = ({
    row: initialRow,
    centerId,
    yearMonth,
    ghostDays,
    generatedAt,
    onClose,
    onOpenMembership,
}: {
    row: DisplayRow;
    centerId: string;
    yearMonth: string;
    ghostDays: number;
    generatedAt?: string;
    onClose: () => void;
    onOpenMembership: (m: SessionReportMembership) => void;
}) => {
    const { month: monthName, at } = useContext(ReportMonthContext);
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    // 카드가 잠시 빠지거나 순서가 바뀌어도 같은 수강권의 펼침과 완료 안내를 상세 창이 보존한다.
    const [expandedMemberships, setExpandedMemberships] = useState<Set<string>>(() => new Set());
    // 조회 상태를 관찰해요. 처리 기록이 있는 상세의 활성 조회는 처리 훅에서 맡아요.
    const detailQuery = useSessionReport({ centerId, yearMonth, ghostDays, coachId: initialRow.coachId, enabled: false });
    const refreshSupportNumbers = async () => {
        const refreshed = await detailQuery.refetch({ cancelRefetch: false });
        if (refreshed.isError || refreshed.data?.status !== 'ok') return false;
        await queryClient.invalidateQueries({ queryKey: supportListKey(centerId) });
        return true;
    };
    const [moveSuccess, setMoveSuccess] = useState<{ text: string; detailUpdatedAt: number } | null>(null);
    const handleMoveDone = ({ notice, lessonTime, targetName, recorded }: LessonMoveCompletion) => {
        setMoveSuccess(notice.tone === 'ok' && recorded
            ? { text: `${lessonTime} 수업을 『${targetName}』로 옮겼어요.`, detailUpdatedAt: detailQuery.dataUpdatedAt }
            : null);
    };
    const refreshedReport = detailQuery.data?.status === 'ok' ? detailQuery.data.report : undefined;
    const refreshedCoach = refreshedReport?.yearMonth === yearMonth && refreshedReport.ghostDays === ghostDays
        ? pickCoachFromReport(refreshedReport, initialRow.coachId) : undefined;
    const hasRefreshedRows = !!refreshedCoach && SESSION_REPORT_SHEETS.every(({ key }) => Array.isArray(refreshedCoach.sheets[key]?.rows));
    const freshRow = hasRefreshedRows ? SESSION_REPORT_SHEETS.flatMap(({ key }) => refreshedCoach!.sheets[key].rows ?? [])
        .find((item) => item.rowKey === initialRow.rowKey) : undefined;
    const row = freshRow ? { ...freshRow, coachName: initialRow.coachName, sheetKey: initialRow.sheetKey } : initialRow;
    const supportRequest = useRowSupport(row);
    const supportGuidance = getSupportGuidance(useContext(SupportContext));
    const titleRef = useRef<HTMLHeadingElement>(null);
    const resolution = useSessionReportResolution({ row, centerId, yearMonth, ghostDays, titleRef });
    const moveRefreshFailed = detailQuery.isError || (!!detailQuery.data && !hasRefreshedRows);
    const moveRefreshed = !!moveSuccess && !moveRefreshFailed && !detailQuery.isFetching
        && detailQuery.dataUpdatedAt !== moveSuccess.detailUpdatedAt;
    // 회원 프로필(통합 회원 상세)로 바로 간다 — 여러 수강권을 한꺼번에 보고 그 화면에서 고칠 수 있게.
    // 같은 창에서 이동한다(새 탭은 2차 비밀번호를 다시 묻는다). 정산 화면은 KeepAlive 라 돌아오면 그대로다.
    const memberId = row.memberIds?.[0] ?? row.memberships.find((m) => m.memberId)?.memberId ?? '';
    const openMemberPage = () => {
        if (!memberId) return;
        onClose();
        navigate(`/member/${memberId}`);
    };
    // 서버에 「수업 보기」 기능이 있을 때만 버튼을 보인다(구서버·확인 실패면 숨김).
    const lessonsAvailability = useSessionReportLessonsAvailability(centerId || undefined);
    const canShowLessons = !!centerId && lessonsAvailability.data === 'available';
    const mismatch = isMismatchRow(row);
    // 무제한권은 서버가 회차 합계·검산값을 일부러 0 으로 둔다. 「맞아요」도 계산식도 보이지 않는다.
    const sessionless = isSessionlessRow(row);
    // 1:2 대표가 다른 강사로 옮겨간 달의 이전 강사 쪽 행 — 수강권은 새 강사 쪽에 있다(계약 §0-11).
    const repMoved = isRepMovedRow(row);
    const usageMemberships = sessionless || repMoved ? [] : row.memberships.filter((m) => hasMembershipSessionUsage(m) && visibleFlags(m.flags).some((flag) => mismatch || flagReviewLevel(flag) !== 'info'));
    const hasUsage = usageMemberships.length > 0;
    const reasons = rowReviewReasons(row);
    const actionReasons = reasons.filter((flag) => flagReviewLevel(flag) !== 'info');
    const infoReasons = reasons.filter((flag) => flagReviewLevel(flag) === 'info')
        .filter((flag, index, all) => !BEFORE_ISSUE_FLAGS.has(flag) || all.findIndex((reason) => BEFORE_ISSUE_FLAGS.has(reason)) === index);
    const topReason = actionReasons[0];
    const topLevel = topReason ? flagReviewLevel(topReason) : 'info';
    const [showProblems, setShowProblems] = useState(false);
    const [showInfo, setShowInfo] = useState(false);
    const disclosureId = React.useId();
    const [showCanceled, setShowCanceled] = useState(false);
    const [highlighted, setHighlighted] = useState<{ membershipId: string } | null>(null);
    const cardsRef = useRef<HTMLDivElement>(null);
    const canceled = (m: SessionReportMembership) => m.endType === 'canceled' || m.endType === 'deleted';
    const canceledCount = row.memberships.filter(canceled).length;
    const differences = row.resolved === true || row.check.diff === 0 || sessionless || repMoved ? [] : row.memberships.flatMap((m, index) => {
        const diff = m.remaining - (m.prevRemaining + m.registeredSessions - m.progressed + m.handoffSessions + m.refundSessions + m.closed);
        return !m.isSessionless && diff !== 0 ? [{ m, number: index + 1, diff }] : [];
    });
    useEffect(() => {
        if (!highlighted) return;
        const card = Array.from(cardsRef.current?.querySelectorAll<HTMLElement>('[data-membership-id]') ?? [])
            .find((node) => node.dataset.membershipId === highlighted.membershipId);
        card?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        card?.focus({ preventScroll: true });
        const timeout = window.setTimeout(() => setHighlighted(null), 1500);
        return () => window.clearTimeout(timeout);
    }, [highlighted]);
    const renderMembership = (m: SessionReportMembership, index: number) => <MembershipCard
        key={m.membershipId} m={m} number={index + 1} highlighted={highlighted?.membershipId === m.membershipId}
        isExpired={row.sheetKey === 'expired' || row.sheetKey === 'ghost'}
        lessonMove={canShowLessons && m.membershipId ? { centerId, yearMonth } : undefined}
        showLessons={expandedMemberships.has(m.membershipId)}
        onToggleLessons={() => setExpandedMemberships((current) => {
            const next = new Set(current);
            if (next.has(m.membershipId)) next.delete(m.membershipId); else next.add(m.membershipId);
            return next;
        })}
        onMoveDone={handleMoveDone} onOpen={m.membershipId ? () => onOpenMembership(m) : undefined} />;
    const problem = row.resolved === true ? '확인한 내용이에요'
        : mismatch ? `기록과 수강권의 남은 회차가 ${formatReportNumber(Math.abs(row.check.diff))}회 달라요`
        : row.remaining < 0 && !sessionless && !repMoved ? `남은 회차가 ${formatReportNumber(row.remaining)}회로 마이너스예요`
        : topReason ? rowReviewIssue(row, topReason)
        : repMoved ? describeRepMoved(row)
        : sessionless ? `무제한 수강권은 ${monthName} 수업만 확인해요`
        : `기록과 수강권의 남은 회차 ${formatReportNumber(row.remaining)}회가 맞아요`;
    const problemTone = row.resolved === true || !topReason ? '#4E5968' : mismatch || row.remaining < 0 ? '#B91C1C' : '#9A4B08';
    return (
        <Modal
            isCloseOutside={!resolution.busy}
            isCloseButton={false}
            onClose={() => { if (!resolution.isBusy()) onClose(); }}
            setIsVisible={() => { if (!resolution.isBusy()) onClose(); }}
            whiteViewWidth="min(800px, calc(100vw - 32px))"
            borderRadius={25}
            modalAlign="center"
        >
            <Col className="w-full min-w-0 max-h-[80vh] overflow-hidden">
                <div data-testid="member-detail-header" className="relative flex max-h-[36vh] shrink-0 flex-col overflow-hidden border-b border-solid border-[#E9EDF2] bg-white">
                <button type="button" aria-label="회원 상세 닫기" disabled={resolution.busy} onClick={() => { if (!resolution.isBusy()) onClose(); }}
                    className="absolute right-[16px] top-[16px] z-20 flex h-[40px] w-[40px] items-center justify-center rounded-[8px] bg-white text-[#6B7684] hover:bg-[#F2F4F6] disabled:opacity-40"><ReportIcon name="close" /></button>
                <div data-testid="member-detail-header-scroll" className="min-h-0 overflow-y-auto overscroll-contain px-[24px] pt-[24px]">
                {moveSuccess && <div role="status" aria-label="수업 옮기기 완료"
                    className="sticky top-0 z-10 mb-[16px] mr-[40px] flex items-start gap-[12px] rounded-[10px] border border-solid border-[#BBF7D0] bg-[#F0FDF4] px-[16px] py-[12px] text-[14px] leading-[22px] text-[#15803D]">
                    <span className="min-w-0 flex-1 break-keep">{moveSuccess.text} {moveRefreshed ? '숫자를 새로 계산했어요.'
                        : moveRefreshFailed ? '숫자를 다시 계산하지 못했어요. 다시 계산해 주세요.' : '숫자를 다시 계산하고 있어요.'}</span>
                    <button type="button" aria-label="수업 옮기기 안내 닫기" onClick={() => setMoveSuccess(null)}
                        className="shrink-0 underline underline-offset-4">닫기</button>
                </div>}
                <Row className="items-center flex-wrap gap-[10px] pr-[40px]">
                    <h2 ref={titleRef} tabIndex={-1} className="text-[22px] font-medium leading-[30px] tracking-[-0.4px] text-[#3D4244]">
                        {row.memberName}
                    </h2>
                    {memberId && (
                        <button
                            type="button"
                            disabled={resolution.busy}
                            onClick={openMemberPage}
                            className="h-[40px] px-[12px] rounded-[8px] border border-solid border-[#D9E6FF] bg-white text-[14px] text-primary hover:bg-[#F4F7FF]"
                        >
                            회원 프로필 보기
                        </button>
                    )}
                </Row>
                <RegularText fontSize={14} lineHeight={20} color="#4E5968" className="mt-[4px] mb-[18px]">
                    {`${row.coachName} 강사 · ${row.sheetKey === 'ghost' ? `${ghostDays}일 넘게 안 옴` : SHEET_SHORT[row.sheetKey]}${
                        row.kindLabel ? ` · ${row.kindLabel}` : ''
                    }`}
                </RegularText>

                <SupportSummary row={row} generatedAt={freshRow ? refreshedReport?.generatedAt : generatedAt} disabled={resolution.busy || detailQuery.isFetching} onRefresh={refreshSupportNumbers} />
                <section aria-label="문제와 할 일" className="pr-[24px]">
                    <h3 className="flex items-start gap-[8px] text-[18px] font-semibold leading-[28px]" style={{ color: problemTone }}>
                        <ReportIcon name={topReason && row.resolved !== true ? 'alert' : 'info'} className="mt-[5px]" />{problem}
                    </h3>
                    {mismatch && !sessionless && !repMoved && <p className="mt-[6px] text-[14px] leading-[22px] text-[#4E5968]">
                        {`${at === '지금' ? '앱에 보이는' : `${at} 기준`} 남은 회차는 ${formatReportNumber(row.remaining)}회예요. 수업 기록으로 세면 ${formatReportNumber(row.check.computedRemaining)}회라 ${formatReportNumber(Math.abs(row.check.diff))}회가 안 맞아요.`}
                    </p>}
                    {differences.length > 0 && <div aria-label="차이 나는 수강권" className="mt-[6px] flex flex-wrap items-center gap-x-[10px] text-[13px] text-[#2B5DB3]">
                        <span>차이 나는 수강권:</span>{differences.map(({ m, number, diff }) => <button key={m.membershipId} type="button" onClick={() => {
                            if (canceled(m)) setShowCanceled(true);
                            setHighlighted({ membershipId: m.membershipId });
                        }}>{`수강권 ${number}(${diff > 0 ? '+' : ''}${formatReportNumber(diff)}회) ›`}</button>)}
                    </div>}
                    {topReason && row.resolved !== true && !(supportRequest && rowReviewAction(row, topReason, supportGuidance) === supportGuidance) &&
                        <p className="mt-[6px] text-[14px] leading-[22px] text-[#4E5968]">{rowReviewAction(row, topReason, supportGuidance)}</p>}
                    {hasRefreshedRows && !freshRow && <p role="status" className="mt-[8px] text-[14px] leading-[22px] text-[#4E5968]">현재 보고서에는 이 회원 기록이 없어요. 회원 목록을 다시 확인해 주세요.</p>}
                </section>
                </div>
                <div data-testid="member-detail-actions" className="flex shrink-0 flex-wrap items-start gap-[10px] bg-white px-[24px] pb-[16px] pt-[14px]">
                    {row.resolved !== true && topLevel === 'ask' ? <SupportAction
                        key={`${centerId}|${yearMonth}|${ghostDays}|${row.rowKey}`} row={row} centerId={centerId} yearMonth={yearMonth} ghostDays={ghostDays}
                        problem={actionReasons.map((reason) => rowReviewIssue(row, reason)).join(' · ')}
                        numbers={sessionless ? `진행 수업 ${times(row.progressed)} · 무제한 수강권`
                            : `남은 회차 ${times(row.remaining)} · 기록으로 센 회차 ${times(row.check.computedRemaining)} · 차이 ${times(Math.abs(row.check.diff))}`}
                        disabled={resolution.busy || detailQuery.isFetching || (hasRefreshedRows && !freshRow)} onRefresh={refreshSupportNumbers}
                        onSuccessFocus={() => titleRef.current?.focus()}
                        fallback={<CopyMemberName name={row.memberName} disabled={resolution.busy} />} />
                        : isCheckNeededRow({ ...row, resolved: false }) && memberId && <button type="button" disabled={resolution.busy} onClick={openMemberPage}
                            className="min-h-[42px] rounded-[10px] bg-[#3D73DD] px-[16px] text-[14px] font-medium text-white disabled:opacity-50">수강권 확인·고치기</button>}
                    {resolution.actions}
                </div>
                </div>
                <div data-testid="member-detail-scroll" className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-[24px] py-[20px]">
                {resolution.banner}
                <Col role="region" aria-label="회원 회차 요약" className="w-full min-w-0 rounded-[14px] bg-[#F7F9FC] p-[16px] mb-[20px] border border-solid border-[#E5E8EB]">
                    <h3 className="text-[14px] font-semibold text-[#4E5968]">숫자 근거</h3>
                    {hasUsage && <p aria-label="문제 수강권 사용 회차" className="mt-[8px] break-words text-[14px] leading-[22px] text-[#4E5968]">
                        쓴 회차/총회차 · {usageMemberships.map((m, index) => <React.Fragment key={m.membershipId}>{index > 0 && ' · '}<span>{m.name} {formatReportNumber(m.usedSessions!)}/{formatReportNumber(m.totalSession!)}</span></React.Fragment>)}
                    </p>}
                    {sessionless && <p className="mt-[8px] text-[14px] text-[#4E5968]">{`무제한 수강권은 회차를 맞춰 세지 않아요. ${monthName} 수업 ${formatReportNumber(row.progressed)}회만 보여 드려요.`}</p>}
                    {!sessionless && !repMoved && (
                        <div className="mt-[14px]">
                            <SessionCountFlow
                                compact
                                emphasizeRemaining
                                prevRemaining={row.prevRemaining}
                                registeredSessions={row.registeredSessions}
                                progressed={row.progressed}
                                handoffSessions={row.handoffSessions}
                                refundSessions={row.refundSessions}
                                closed={row.otherAdjustments?.closed ?? 0}
                                remaining={row.check.computedRemaining}
                                handoffLabel={(row.handoffs ?? []).some(isCompanionRepMove) ? '강사 간 넘김·1:2 대표 이동' : '강사 간 넘김'}
                                comparison={mismatch ? { remaining: row.remaining, diff: row.check.diff } : undefined}
                                // 숫자가 맞아도 직접 고칠 것·라포가 확인할 것이 있으면 초록 ✓로 「괜찮다」고 보이지 않는다
                                matched={!mismatch && !rowReviewReasons(row).some((flag) => ['fix', 'ask'].includes(flagReviewLevel(flag)))}
                            />
                        </div>
                    )}
                    {!sessionless && repMoved && (
                        <RegularText fontSize={14} lineHeight={19} color="#4E5968" className="mt-[2px]">
                            {`지난달 남은 ${formatReportNumber(row.prevRemaining)} + 새로 판 ${formatReportNumber(row.registeredSessions)} − ${monthName} 수업 ${formatReportNumber(row.progressed)} + ${
                                (row.handoffs ?? []).some(isCompanionRepMove) ? '강사 간 넘김·1:2 대표 이동' : '강사 간 넘김'
                            } (${formatReportNumber(row.handoffSessions)}) + 환불 (${formatReportNumber(row.refundSessions)}) + 취소·정리 (${formatReportNumber(row.otherAdjustments?.closed ?? 0)}) = ${formatReportNumber(row.check.computedRemaining)}`}
                        </RegularText>
                    )}
                </Col>
                <div className="mb-[12px] flex flex-wrap items-center gap-x-[16px] text-[14px] text-[#2B5DB3]">
                    {row.resolved !== true && actionReasons.length > 0 && <button type="button" aria-expanded={showProblems} aria-controls={`${disclosureId}-problems`} onClick={() => setShowProblems((value) => !value)}>{showProblems ? '▾' : '▸'} 문제 자세히 ›</button>}
                    {infoReasons.length > 0 && <button type="button" aria-expanded={showInfo} aria-controls={`${disclosureId}-info`} onClick={() => setShowInfo((value) => !value)}>{showInfo ? '▾' : '▸'} 참고 안내 {infoReasons.length}개 ›</button>}
                </div>
                {row.resolved !== true && actionReasons.length > 0 && <div id={`${disclosureId}-problems`} hidden={!showProblems} className="mb-[16px]">
                    <ReviewReasons row={row} flags={actionReasons} />
                </div>}
                {infoReasons.length > 0 && <div id={`${disclosureId}-info`} hidden={!showInfo} className="mb-[20px]">
                    <ReviewReasons row={row} flags={infoReasons} centerId={centerId} loadLessonDates={showInfo && canShowLessons} />
                </div>}

                {repMoved && row.memberships.length === 0 ? (
                    <RegularText fontSize={14} lineHeight={20} color="#4E5968">
                        수강권 상세는 새 대표 강사의 회원 표에서 볼 수 있어요.
                    </RegularText>
                ) : (
                    <MediumText fontSize={16} lineHeight={22} color="#3D4244" className="mb-[8px]">
                        {`수강권 ${row.memberships.length}개`}
                    </MediumText>
                )}
                <div ref={cardsRef} className="flex flex-col gap-[16px]">
                    {row.memberships.map((m, index) => !canceled(m) && renderMembership(m, index))}
                    {canceledCount > 0 && <div className="rounded-[12px] bg-[#F4F5F7] p-[12px]">
                        <button type="button" aria-expanded={showCanceled} onClick={() => setShowCanceled((value) => !value)} className="w-full text-left text-[14px] text-[#4E5968]">
                            {showCanceled ? '▾' : '▸'} 취소된 수강권 {canceledCount}개 {showCanceled ? '접기' : '보기'} ›
                        </button>
                        {showCanceled && <div className="mt-[12px] flex flex-col gap-[16px]">{row.memberships.map((m, index) => canceled(m) && renderMembership(m, index))}</div>}
                    </div>}
                </div>
                </div>
            </Col>
        </Modal>
    );
};

const MembershipCard = ({
    m,
    number,
    highlighted,
    isExpired,
    onOpen,
    lessonMove,
    showLessons,
    onToggleLessons,
    onMoveDone,
}: {
    m: SessionReportMembership;
    number: number;
    highlighted?: boolean;
    isExpired: boolean;
    onOpen?: () => void;
    /** 있으면 「수업 보기」 — 이 수강권의 수업을 펼쳐 다른 수강권으로 옮길 수 있다. */
    lessonMove?: { centerId: string; yearMonth: string };
    showLessons: boolean;
    onToggleLessons: () => void;
    onMoveDone: (completion: LessonMoveCompletion) => void;
}) => {
    const lessonsRef = useRef<HTMLDivElement>(null);
    const scrollLessonsRequested = useRef(false);
    useEffect(() => {
        if (!scrollLessonsRequested.current || !showLessons) return;
        scrollLessonsRequested.current = false;
        lessonsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, [showLessons]);
    const tags = [
        m.issueKind === 'reRegistration' ? '재등록' : '신규',
        MEMBERSHIP_KIND_LABEL[m.kind] ?? '기타 수강권',
        m.isShared ? '공유' : '',
        m.companionGroupId ? (m.isCompanionPrimary ? '1:2 대표' : '1:2 상대') : '',
        m.isSessionless ? '무제한' : '',
        m.isServiceSession ? '서비스' : '',
    ].filter(Boolean);
    const endTag = m.endType ? `${END_TYPE_LABEL[m.endType] ?? '종료'}${m.endAtEstimated ? '(추정)' : ''}` : '';
    const facts = [
        m.expirationDate ? `만료 ${m.expirationDate}${m.expiryPolicy === 'COUNT' ? '(기간 지나도 사용 가능)' : ''}` : '',
        m.lastLessonAt
            ? `마지막 출석 ${formatKst(m.lastLessonAt, 'YYYY-MM-DD')}${
                  m.daysSinceLastLesson !== null ? ` (${formatReportNumber(m.daysSinceLastLesson)}일 전)` : ''
              }`
            : '출석 기록 없음',
    ].filter(Boolean);
    const { month: monthName } = useContext(ReportMonthContext);
    // 표시한 단계의 산수만 비교한다. 서버 잔여·검산·서버에 보내는 값은 바꾸지 않는다.
    const displayedTotal = m.prevRemaining + m.registeredSessions - m.progressed + m.handoffSessions + m.refundSessions + m.closed;
    const comparison = displayedTotal !== m.remaining ? { remaining: m.remaining, diff: m.remaining - displayedTotal } : undefined;
    const flags = visibleFlags(m.flags)
        .filter((flag, index, all) => !BEFORE_ISSUE_FLAGS.has(flag) || all.findIndex((reason) => BEFORE_ISSUE_FLAGS.has(reason)) === index);
    return (
        <Col role="region" tabIndex={-1} data-membership-id={m.membershipId} aria-label={`수강권 ${number}`} className={`${highlighted ? 'session-report-membership-highlight' : ''} scroll-mt-[8px] w-full min-w-0 rounded-[14px] border border-solid border-[#E4E4E4] p-[16px] ${m.endType || isExpired ? 'bg-[#F8F9FB]' : 'bg-white'}`}>
            <Row className="items-center flex-wrap gap-[6px] mb-[10px]">
                <MediumText fontSize={14} lineHeight={19} color="#4E5968" className="mr-[4px]">{`수강권 ${number}`}</MediumText>
                {endTag && <span className={`inline-flex items-center rounded-[5px] px-[8px] py-[3px] text-[14px] font-noto-medium ${'bg-[#F2F4F6] text-[#4E5968]'}`}>{endTag}</span>}
                {/* 기간이 지나도 쓸 수 있는 수강권(COUNT)은 「만료」라고 하지 않는다 — 바로 아래 「기간 지나도 사용 가능」과 반대 말이 된다 */}
                {isExpired && (m.expiryPolicy === 'COUNT'
                    ? <span className="rounded-[5px] bg-[#F2F4F6] px-[8px] py-[3px] text-[14px] font-noto-medium text-[#4E5968]">기간 지남</span>
                    : <span className="rounded-[5px] bg-WARNING_50 px-[8px] py-[3px] text-[14px] font-noto-medium text-WARNING">만료</span>)}
                <Row className="ml-auto flex-wrap gap-[6px]">
                    {lessonMove && (
                        <button type="button" aria-expanded={showLessons} onClick={() => {
                            scrollLessonsRequested.current = !showLessons;
                            onToggleLessons();
                        }}
                            className="min-h-[40px] px-[10px] rounded-[7px] border border-solid border-[#D9E6FF] bg-white cursor-pointer hover:bg-[#F4F7FF]">
                            <RegularText fontSize={14} lineHeight={19} color={colors.primary}>{showLessons ? '수업 접기' : '수업 보기'}</RegularText>
                        </button>
                    )}
                    {onOpen && (
                        <button type="button" onClick={onOpen}
                            className="min-h-[40px] px-[10px] rounded-[7px] border border-solid border-[#D9E6FF] bg-white cursor-pointer hover:bg-[#F4F7FF]">
                            <RegularText fontSize={14} lineHeight={19} color={colors.primary}>수강권 열기</RegularText>
                        </button>
                    )}
                </Row>
            </Row>
            <Row className="items-center flex-wrap gap-[6px] mb-[6px]">
                <MediumText fontSize={17} lineHeight={24} color="#3D4244" className="mr-[4px] break-words min-w-0">
                    {m.name}{m.startAt ? ` · ${formatKst(m.startAt, 'M월 D일')} 발급` : ' · 발급일 확인 필요'}
                </MediumText>
                {tags.map((tag) => (
                    <span
                        key={tag}
                        className="inline-flex items-center px-[7px] py-[1px] rounded-[4px] text-[14px] font-noto-medium bg-[#ECF1FC] text-[#2B529D]"
                    >
                        {tag}
                    </span>
                ))}
            </Row>
            <RegularText fontSize={14} lineHeight={19} color="#4E5968" className="mb-[14px]">
                {facts.join(' · ')}
            </RegularText>
            {hasMembershipSessionUsage(m) && <div className="mb-[16px]">
                <MembershipSessionUsage membership={m} isExpired={isExpired} />
            </div>}
            {m.isSessionless ? (
                <RegularText fontSize={14} lineHeight={19} color="#4E5968">{`무제한 수강권은 회차를 맞춰 세지 않아요. ${monthName} 수업 ${formatReportNumber(m.progressed)}회만 보여 드려요.`}</RegularText>
            ) : (
                <SessionCountFlow compact prevRemaining={m.prevRemaining} registeredSessions={m.registeredSessions}
                    progressed={m.progressed} handoffSessions={m.handoffSessions} refundSessions={m.refundSessions}
                    closed={m.closed} remaining={comparison ? displayedTotal : m.remaining} comparison={comparison} />
            )}
            {REVIEW_LEVEL_ORDER.map((level) => {
                const levelFlags = flags.filter((flag) => flagReviewLevel(flag) === level);
                if (!levelFlags.length) return null;
                const tone = REVIEW_TONES[level];
                return (
                    <Row key={level} role="note" aria-label={tone.label}
                        className="mt-[12px] items-start gap-[8px] rounded-[8px] border border-solid px-[12px] py-[10px]"
                        style={{ backgroundColor: level === 'info' ? `${tone.bg}80` : tone.bg, borderColor: level === 'info' ? `${tone.border}80` : tone.border }}>
                        <span aria-hidden="true" className="shrink-0 text-[16px] leading-[20px]" style={{ color: tone.text }}>{level === 'fix' ? '⚠' : 'ⓘ'}</span>
                        <RegularText fontSize={14} lineHeight={20} color={tone.text} className="break-keep">
                            {levelFlags.map((flag) => BEFORE_ISSUE_FLAGS.has(flag) ? '발급 전 수업이 있어요' : flag === 'NEGATIVE_REMAINING'
                                ? describeMembershipSessionOverage(m) ?? describeFlag(flag) : describeFlag(flag)).join(' · ')}
                        </RegularText>
                    </Row>
                );
            })}
            {lessonMove && showLessons && <div ref={lessonsRef} tabIndex={-1} aria-label={`수강권 ${number} 수업 목록`} className="scroll-mt-[8px]"><LessonMovePanel centerId={lessonMove.centerId} membership={m} yearMonth={lessonMove.yearMonth} onDone={onMoveDone} /></div>}
        </Col>
    );
};

// ───────────────────────────── 작은 조각들 ─────────────────────────────

/** 강사 표는 회원별 행 수와 가장 급한 이유를 보여 준다. 이유별 건수와 구분한다. */
const REVIEW_BADGE_HINT = { fix: '직접 고칠 것 포함', ask: '라포가 확인할 것 포함', review: '확인만 하면 되는 것', info: '할 일 없음' } as const;
/** 강사 표 「확인할 일」 칸 — 가장 급한 단계의 이름·색, 회원별 행 수. */
const ReviewCountBadge = ({ count }: { count?: number }) => <span className="inline-flex items-center whitespace-nowrap text-[14px] font-semibold text-[#2B5DB3]">
    {count === undefined ? <ReviewPending /> : `${displayNumber(count)}건 확인 필요`}
</span>;

const WarningBadge = ({ text }: { text: string }) => (
    <span
        className="inline-flex items-center justify-center px-[8px] py-[2px] rounded-[4px] text-[14px] font-semibold whitespace-nowrap"
        style={{ backgroundColor: WARNING_BG, color: WARNING_TEXT }}
    >
        {text}
    </span>
);

const CHIP_TONE = {
    warning: { bg: WARNING_BG, border: WARNING_BORDER, text: WARNING_TEXT, icon: '#E0A100' },
    ok: { bg: '#E6F8F0', border: '#BDEBD5', text: '#067A46', icon: '#15BA72' },
    neutral: { bg: '#F4F5F7', border: '#F4F5F7', text: '#3D4244', icon: '#848F9A' },
};

const Chip = ({
    tone,
    icon,
    text,
    actionText,
    onClick,
    muted = false,
}: {
    tone: keyof typeof CHIP_TONE;
    icon?: 'SvgWarningInCircle' | 'SvgCheck';
    text: string;
    actionText?: string;
    onClick?: () => void;
    muted?: boolean;
}) => {
    const c = CHIP_TONE[tone];
    return (
        <button
            type="button"
            className={`flex items-center h-[40px] px-[14px] rounded-[20px] border border-solid ${onClick ? 'cursor-pointer' : ''} ${muted ? 'opacity-50 hover:opacity-80' : ''}`}
            style={{ backgroundColor: c.bg, borderColor: c.border }}
            onClick={onClick}
        >
            {icon && <SvgIcon name={icon} size={16} fill={c.icon} className="mr-[7px]" />}
            <MediumText fontSize={15} lineHeight={21} color={c.text} letterSpacing={-0.3}>
                {text}
            </MediumText>
            {actionText && (
                <Row className="items-center ml-[10px]">
                    <RegularText fontSize={14} lineHeight={20} color={c.text} className="opacity-80">
                        {actionText}
                    </RegularText>
                    <SvgIcon
                        name={'SvgRightStrokeArrow'}
                        size={11}
                        fill={colors.trans}
                        stroke={c.text}
                        strokeWidth={1.4}
                        className="ml-[3px]"
                    />
                </Row>
            )}
        </button>
    );
};

const SheetTab = ({
    title,
    count,
    isSelected,
    tone,
    onClick,
}: {
    title: string;
    count: number;
    isSelected: boolean;
    tone?: 'warning';
    onClick: () => void;
}) => {
    return <button type="button" role="tab" aria-selected={isSelected} onClick={onClick}
        className={`relative flex h-[53px] shrink-0 items-center gap-[7px] text-[14px] ${isSelected ? 'font-[650] text-[#3D73DD] after:absolute after:bottom-[-1px] after:left-0 after:right-0 after:h-[2px] after:rounded-[2px] after:bg-[#3D73DD]' : 'font-[550] text-[#4E5968]'}`}>
        {title}<span className="text-[13px] tabular-nums">{displayNumber(count)}</span>
    </button>;
};

const StateMessage = ({
    title,
    description,
    actionText,
    onAction,
    children,
}: {
    title: string;
    description: string;
    actionText?: string;
    onAction?: () => void;
    children?: React.ReactNode;
}) => (
    <Col className="w-full items-center min-h-[420px] pb-[40px]">
        <MediumText fontSize={20} lineHeight={29} letterSpacing={-0.3} color="#3D4244" className="mb-[8px] mt-[100px]">
            {title}
        </MediumText>
        <RegularText fontSize={16} lineHeight={24} color="#4E5968">
            {description}
        </RegularText>
        {actionText && onAction && (
            <Row
                className="mt-[24px] items-center justify-center bg-[#F2F4F6] rounded-[7px] px-[18px] h-[40px] cursor-pointer"
                onClick={onAction}
            >
                <RegularText fontSize={15} lineHeight={22} color="#3D4244">
                    {actionText}
                </RegularText>
            </Row>
        )}
        {children}
    </Col>
);

const LoadingSkeleton = () => (
    <Col className="w-full">
        <div className="w-full h-[112px] rounded-[16px] bg-[#F4F7FF] animate-pulse mb-[24px] px-[28px] py-[28px] flex flex-col">
            <MediumText fontSize={20} lineHeight={28} color="#4E5968" letterSpacing={-0.3}>
                센터 전체 수업 기록으로 회차를 세고 있어요
            </MediumText>
            <RegularText fontSize={14} lineHeight={20} color="#4E5968" className="mt-[6px]">
                회원이 많은 센터는 30초쯤 걸릴 수 있어요
            </RegularText>
        </div>
        <div className="w-full h-[128px] rounded-[4px] bg-[#F4F5F7] animate-pulse mb-[20px]" />
        <Row className="gap-[10px] mb-[44px]">
            {[260, 140, 200].map((w) => (
                <div key={w} className="h-[40px] rounded-[20px] bg-[#F4F5F7] animate-pulse" style={{ width: w }} />
            ))}
        </Row>
        {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="w-full h-[52px] bg-[#F8F9FB] animate-pulse mb-[6px] rounded-[4px]" />
        ))}
    </Col>
);

const PaginationBar = ({
    firstPage,
    nextPage,
    canNextPage,
    previousPage,
    canPreviousPage,
    pageIndex,
    totalPageIndex,
}: {
    firstPage: () => void;
    previousPage: () => void;
    canPreviousPage: boolean;
    nextPage: () => void;
    canNextPage: boolean;
    pageIndex?: number;
    totalPageIndex?: number;
}) => {
    // 공용 Table 은 canPreviousPage/canNextPage 에 「못 가는가」를 넘긴다(기존 화면들과 같은 해석).
    if ((totalPageIndex ?? 1) <= 1) return null;
    return (
        <Row className="absolute items-center flex right-0 bottom-0 mb-[10px]">
            <RegularText fontSize={14} lineHeight={20} color="#4E5968" className="mr-[16px]">
                {`${(pageIndex ?? 0) + 1} / ${totalPageIndex}`}
            </RegularText>
            <RegularText
                fontSize={16}
                lineHeight={23}
                color="#3D4244"
                className={`mr-[16px] ${canPreviousPage ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                onClick={() => !canPreviousPage && firstPage()}
            >
                처음으로
            </RegularText>
            <button
                className="flex flex-row items-center mr-[13px] bg-[#F4F5F7] rounded-[7px] px-[19px] py-[7px] disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={previousPage}
                disabled={canPreviousPage}
            >
                <SvgIcon
                    name={'SvgLeftStrokeArrow'}
                    size={15}
                    fill={colors.trans}
                    stroke={'#70747C'}
                    strokeWidth={1.2}
                    className="mr-[7px]"
                />
                <RegularText fontSize={16} lineHeight={23} color="#3D4244">
                    이전
                </RegularText>
            </button>
            <button
                className="flex flex-row items-center bg-[#F4F5F7] rounded-[7px] px-[19px] py-[7px] disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={nextPage}
                disabled={canNextPage}
            >
                <RegularText fontSize={16} lineHeight={23} color="#3D4244" className="mr-[7px]">
                    다음
                </RegularText>
                <SvgIcon name={'SvgRightStrokeArrow'} size={15} fill={colors.trans} stroke={'#70747C'} strokeWidth={1.2} />
            </button>
        </Row>
    );
};
